// Learning mode — the Learn tab's tray and rung-ladder session.
// Design of record: docs/design/mockups/learning-mode-mockup.html (interactive mockup).
//
// The TRAY shares Practice's rendering: main.js paints this module's staged SENSE CARDS (getTray)
// as lean rows (buildEntryElement) in the shared #defId box — identical sound / add-to-list / IPA /
// rich-drill — and this module only layers each card's learning-status badge (decorateTray) and the
// "Start Learning!" / Clear band (renderCta into #learnControlBandId). NO duplicate row markup.
// The live SESSION (meet card → recognition ×2 → look-alikes → first letters → sound-only →
// graduation → recap) is the one thing rendered here, inside #learnId (#learnBoxId).
//
// The learnable unit is the SENSE: a polysemous word stages one card per meaning (adjacent), each
// with its own ladder state, gloss and example. The word (term) still owns the left column —
// headword, IPA, sound — and add-to-list stays term-scoped.
//
// main.js owns the chrome (tab switching, box show/hide) and provides the hooks: window.openLearnTab,
// window.learnHandoffToPractice, window.learnSessionChrome (swap tray↔session box and switch the Word
// Lists panel's adds off for a session — the mode tabs STAY, first-run R15), window.renderLearnTray (repaint the tray) and
// window.senseCardsOf (the shared cluster→cards rule). This module exposes window.LRN (addWords /
// removeWord / showTray / getTray / getDeletedSenseIds / absorbDeleted / renderCta / decorateTray /
// reset / leave / maybeNudge / trayCount).
// Server contract: /api/learning/sense-batch-start | sense-event | sense-status.
//
// Tone rules (from the design): no timer, no red, a miss just recedes; every miss re-teaches
// (word + gloss + sound); a word never ejects from the session.
(function () {
	"use strict";

	var RUNG_COUNT = 7;      // R0 meet … R5 sound … R6 full-type; rung 7 = graduated to the drill
	// The rungs a session actually ASKS. Every session climbs all seven but one: the guided tour's
	// first Learn session skips R2 (pick the word for the meaning, the mirror of R1) — owner ruling
	// R10, docs/project/first-run-redesign.md, as revised 2026-10-08: the six-card look-alike rung
	// (R3) is asked again, the sound rung stays. Fixed per session at Start (session.rungs).
	// ⚠️ `learnGuided_dots` says "6" in every bundle — the two change together.
	var FULL_RUNGS = [0, 1, 2, 3, 4, 5, 6];
	var GUIDED_RUNGS = [0, 1, 3, 4, 5, 6];
	var WINDOW_SIZE = 5;     // rolling window of concurrently active cards
	var REPEAT_GAP = 2;      // min items between two visits of the same card

	var tray = [];             // sense cards ({term, senseId, pos, translations, cluster}) in tray order
	var deletedSenseIds = {};  // senseId -> termId: cards ×-removed this session (saved soft-deleted)
	var statusBySenseId = {};  // senseId -> {rung, started, graduated, seenInDrill}
	var session = null;        // live session state, or null
	// Does this session's term language accept a bare base letter where the word has an umlaut
	// ("kummern" for "kümmern")? Sent by sense-batch-start from the SAME server rule Practice and
	// the dictionary follow (learn.search.german.single-letter-substitution). Default false = strict:
	// only the digraph (ue/oe/ae/ss) or the umlaut itself, so a spelling Learn accepts on the typing
	// rungs is a spelling Practice will accept when the word graduates into the drill.
	var foldAnswers = false;
	var pendingPractice = null; // cards just graduated by a finished session, waiting to be drilled:
	                            // turns the tray's Start hero into "Go to the practice now!" until taken

	window.LRN = {
		addWords: addWords,
		removeWord: removeWord,
		showTray: showTray,
		getTray: getTray,          // main.js renders these cards through the Practice path
		getDeletedSenseIds: getDeletedSenseIds,   // main.js sends these with every tray save
		absorbDeleted: absorbDeleted,             // rehydrate hands back the stored ×-removals
		renderCta: renderCta,      // main.js repaints the Start/Clear band after a tray render
		decorateTray: decorateTray, // main.js (displayAll) badges the shared rows by sense id
		decorateTermRows: decorateTermRows,  // …and the same dots on any rendered WORD-LIST rows
		reset: reset,
		clearTray: clearTray,
		leave: leave,
		backToTray: backToTray,    // the "back to the words" exit, for the walk's "stop the tour"
		toPractice: toPractice,    // the walk's Practice door: the words go to Practice with the reader
		// The walk's Back on a flipped meet card: back to its unflipped face (renderMeet's meetUnflip).
		canUnflipMeet: function () { return !!meetUnflip; },
		unflipMeet: function () { if (!meetUnflip) { return false; } meetUnflip(); return true; },
		maybeNudge: maybeNudge,
		trayCount: function () { return tray.length; }
	};

	// ── shared helpers ───────────────────────────────────────────────────────────

	function root() { return $("#learnId"); }
	function ctaHost() { return $("#learnControlBandId"); }
	function langToCode() { return $("#langToValId").attr("data-lang-code"); }
	function langFromCode() { return $("#langFromValId").attr("data-lang-code"); }

	// A live session swaps the tray box for the session box and switches the Word Lists panel's adds
	// off (main.js learnSessionChrome). The MODE TABS STAY (owner ruling R15,
	// docs/project/first-run-redesign.md: hiding them "breaks the UI and confuses… we have a lot of
	// real estate") — which is also why this has to remember that it took the chrome: a tab pressed
	// on the RECAP leaves through leave() with `session` already null (finishSession clears it), and
	// asking `session` there left the panel's adds switched off under the tab the reader went to.
	var chromeHeld = false;

	function sessionChrome(active) {
		chromeHeld = !!active;
		if (window.learnSessionChrome) { window.learnSessionChrome(active); }
	}

	function termMode() { return !!(window.isTermUnitMode && window.isTermUnitMode()); }

	// Every sense id one card stages: its own sense (sense mode) or all the word's senses (term mode).
	function cardSenseIds(card) {
		if (!card) { return []; }
		if (card.senseIds && card.senseIds.length) { return card.senseIds; }
		return card.senseId != null ? [card.senseId] : [];
	}

	// Sense ids a session may offer: banned-as-known senses stay in the tray (grayed by main.js) but
	// are never sent to batch-start. In term mode a card carries ALL the word's senses (the whole term
	// is learned at once). The server skips known/graduated anyway (defense in depth).
	function traySenseIds() {
		var ids = [];
		for (var i = 0; i < tray.length; i++) {
			var sids = cardSenseIds(tray[i]);
			for (var s = 0; s < sids.length; s++) {
				if (!isKnown(sids[s])) { ids.push(sids[s]); }
			}
		}
		return ids;
	}

	function isKnown(senseId) {
		return !!(window.isKnownSense && window.isKnownSense(senseId));
	}

	// Tray cards a session would actually teach — the Start button's count and enable state.
	// Legacy senseId-null cards count (they run session-only), banned-as-known cards don't.
	function usableTrayCount() {
		var n = 0;
		for (var i = 0; i < tray.length; i++) {
			var c = tray[i];
			if (c && !(c.senseId != null && isKnown(c.senseId))) { n++; }
		}
		return n;
	}

	// A card's dedup key: its sense, or the bare term for a legacy card without senses (those can
	// be staged for the session but have no ladder / no persistence — accepted for legacy pairs).
	function cardKey(card) {
		return card.senseId != null ? "s" + card.senseId : "t" + (card.term && card.term.id);
	}

	function displayTerm(w) {
		return (w.article ? w.article + " " : "") + w.term;
	}

	function ipaText(ipa) {
		if (!ipa) { return ""; }
		return ipa.charAt(0) === "/" ? ipa : "/" + ipa + "/";
	}

	// Sound (resolve/cache/prefetch/play) is the shared PlSound module (static/js/sound.js) —
	// every surface that plays a term's pronunciation uses the same cache instead of its own copy.
	function stopTermSound() { window.PlSound.stop(); }

	// Called once, right when a session's word list is known (before the first card renders): warms
	// every distinct word's sound — url AND bytes — up front instead of one-at-a-time as the
	// ladder reaches it. A session's tray is small (well under PlSound's prefetch cap), so no cap
	// or stagger needed here — unlike a Word List's viewport prefetch.
	function prefetchSessionSounds(words) {
		var items = [];
		for (var i = 0; i < words.length; i++) {
			var w = words[i].w;
			if (w && w.termId != null) { items.push({ termId: w.termId, variantId: w.ipaVariantId }); }
		}
		window.PlSound.prefetchMany(items);
	}

	// Which word a meet card's reveal actually LOOKS UP: a conjugated form teaches the head verb's
	// entry (see meetVerbForm), every other card its own term. One definition, so the prefetch below
	// and the render in renderMeet can never warm and then ask for different words.
	function meetLookupTerm(w) {
		return studiesHeadVerb(w) ? w.verbLemma : w.term;
	}

	// The sibling of prefetchSessionSounds, for the rich entries rather than the audio. Every card
	// that starts at rung 0 gets a meet card, and a meet card's reveal ALWAYS opens the word's full
	// rich view — so that lookup is settled before the session has drawn a single card, and waiting
	// for the flip to ask for it is what puts a "Looking up…" strip in front of the learner. A Learn
	// card always carries senseIds, i.e. its rich entry is stored, so this is a DB read and never a
	// paid generation. Capped and staggered (unlike the sounds) because a big quick-start tray would
	// otherwise fire a burst of fat entry requests competing with the first card's own.
	function prefetchSessionRichEntries(words) {
		if (!window.prefetchRichEntries) { return; }
		var terms = [];
		for (var i = 0; i < words.length; i++) {
			if (words[i].rung === 0) { terms.push(meetLookupTerm(words[i].w)); }
		}
		window.prefetchRichEntries(terms, { cap: 12, staggerMs: 120 });
	}

	// variantId (optional) = the card's heteronym pronunciation variant (0038): the ladder
	// speaks the sense being learned, not the term default.
	function playTermSound(termId, variantId) { window.PlSound.play(termId, variantId); }

	// Two sound-icon sizes only (app-wide rule): the BIG one (matches the rich-view headword) for a
	// single word shown with room around it — the meet card, the word→meaning prompt, the sound rung;
	// the SMALL one for tight spots — the compact miss chip. Same volume glyph, same borderless round
	// face + orange-on-hover; only the size differs.
	function soundButton(termId, big, variantId) {
		return $("<button>").attr("type", "button")
			.addClass(big ? "lrnSndLgClass" : "lrnSndClass")
			.append($("<span>").addClass("pl-ic pl-ic-volume"))   // the app-wide volume icon, not a 🔊 emoji
			.assignTooltip("sound_tooltip")   // "Sound [Space]" — same tip + shortcut as the Dictionary tab
			.click(function (e) { e.stopPropagation(); playTermSound(termId, variantId); });
	}

	// The microphone that stands with that speaker (ai-features-plan.md §1 decision 1). Learn has no
	// Add control, so the pair here is Mic · Sound — and it is planted only on a REVEALED word: the
	// popup prints the word, which on a rung still being answered would be the answer.
	function micButton(w, big) {
		if (!w || w.termId == null) { return null; }
		return $("<button>").attr("type", "button")
			.addClass("pl-btn-toggle pronMicClass " + (big ? "pronMicLrnLgClass" : "pronMicSmClass"))
			.attr("aria-pressed", "false")
			.append($("<span>").addClass("pl-ic pl-ic-mic"))
			.assignTooltip("pronMic_tooltip")
			.click(function (e) {
				e.stopPropagation();
				PRONOUNCE.open(this, {
					termId: w.termId, text: displayTerm(w), article: w.article || null,
					ipa: window.plPopupIpa ? window.plPopupIpa(w.term, w.ipa) : w.ipa,
					ipaVariantId: w.ipaVariantId || null
				});
			});
	}

	// NFD + strip combining marks: ä→a, ü→u, é→e. NOT an answer check — the typing rungs compare
	// through typingVariants/typedNorm, which keep the accent because the strict umlaut rule turns
	// on it. This one only recognises structural words in a term (the leading reflexive "sich"),
	// where being generous costs nothing.
	function fold(s) {
		var t = s.toLowerCase();
		if (t.normalize) { t = t.normalize("NFD").replace(/[̀-ͯ]/g, ""); }
		return t.replace(/ß/g, "ss");
	}

	function shuffle(arr) {
		var a = arr.slice();
		for (var i = a.length - 1; i > 0; i--) {
			var j = Math.floor(Math.random() * (i + 1));
			var tmp = a[i]; a[i] = a[j]; a[j] = tmp;
		}
		return a;
	}

	// n random distractors from the stored pool — fresh subset on every ask (see LearningService).
	function deal(pool, n) {
		return shuffle(pool || []).slice(0, n);
	}

	// R3's seventh card — "None of these" (docs/design/learn-distractor-quality.md §7). R3 ONLY: it
	// is the six-way exact-form rung, so "is the word you know actually among these spellings?" is a
	// sharp, fair question, and it kills the "one of them must be it" scan outright. On the four-card
	// meaning rungs the same option would read as a trick rather than a test.
	//
	// Two rules, and both were settled against the design's text (owner, 2026-07-31):
	//
	//  - SHOWN on every R3 card except the learner's FIRST of the session. The design said "never on
	//    a word's first R3 exposure", which is unreachable: a word normally reaches R3 once, passes,
	//    and goes to R4 — only a miss at R4 brings it back, so a per-WORD rule leaves the feature
	//    dormant. Per SESSION keeps the design's actual point, which is that first contact with the
	//    six-way format should be a straight discrimination.
	//  - CORRECT on roughly one shown card in NONE_RATE. It cannot be "shown 1 in 6 and always
	//    right", because then its presence WOULD BE the answer — the opposite of the point, which is
	//    that with a real "none" always on the table, half-recognising one form is no longer enough.
	//    A card answered by an absence is safe here because the reveal still teaches the word:
	//    shown, pronounced and named, exactly as a miss does (showRevealTaught).
	var NONE_RATE = 6;

	// `mark`: an optional GARDEN.markIcon name appended after the text. The "ready" toast used
	// to end in an emoji baked into the i18n string; it now gets the real sprout image, like every
	// other place in the app that names the Learning end of the ladder.
	// The widget itself moved to msgbox.js (2026-08-02) as window.showToast — it was never
	// Learn-specific, and "that worked, carry on" is wanted all over the app. This wrapper stays so
	// the call sites below read the same as they always did.
	function toast(msg, mark) {
		showToast(msg, mark);
	}

	function genderClass(article) {
		if (article === "der") { return "rich-gender-M"; }
		if (article === "die") { return "rich-gender-F"; }
		if (article === "das") { return "rich-gender-N"; }
		return "";
	}

	// ── public API ───────────────────────────────────────────────────────────────

	// Full wipe (language change abandons the tray + any live session). Restore the session chrome
	// first if a session screen held it, so the session box and the panel's switched-off adds don't
	// outlive it under the new pair.
	function reset() {
		if (chromeHeld) { sessionChrome(false); }   // a session screen or its recap — see sessionChrome
		tray = []; deletedSenseIds = {}; statusBySenseId = {}; session = null; pendingPractice = null;
	}

	// Empty the tray for real: wipe it, forget the ×-removals it was remembering, and SAVE.
	// Deliberately paints nothing — the tray shares #defId with Dictionary and Practice, so a
	// repaint from off-tab would blow away whatever list is actually on screen. Callers that
	// are ON the Learn tab follow up with showTray() (the Clear pill does; the help hub's tour
	// replay goes through main.js's resetTraysForReplay, which decides per active tab).
	// Clearing deletedSenseIds mirrors the Practice twin's Clear ("an emptied tray has no
	// partial words left to remember") — the two Clears stay twins.
	function clearTray() {
		pendingPractice = null;
		tray = [];
		deletedSenseIds = {};
		persistTray();
	}

	// Leaving the tab mid-session: rung state is already persisted per event — just drop the
	// runtime. The next Learn visit lands on the tray, resumable. If a session screen was UP (a tab
	// pressed mid-session or on the recap — the tabs stay on screen through both since R15 — or
	// browser Back, which routes through applyDictChrome/randomWordsView → here), hand back the
	// chrome it took, so the Word Lists panel's adds don't stay off under the tab we land on. A no-op
	// when nothing is running (ordinary tab switches away from Learn).
	function leave() {
		if (window.endLearnDrill) { window.endLearnDrill(); }   // kill any live meet-card reveal drill
		disarmSpace();
		stopTermSound();   // don't let a word keep sounding after we've left the session
		if (chromeHeld) { sessionChrome(false); }
		session = null;
		meetUnflip = null;
	}

	// …and the LOUD half, which is what the session screen's own "back to the words" pill does: drop
	// the session, hand the chrome back (mode tabs, the tray box, the panel's add affordances) AND
	// repaint the tray into the box that chrome just restored. `leave()` above is for a caller that
	// is about to fill that box itself (a tab switch); this one is for a caller that wants the plain
	// Learn tab and nothing else.
	//
	// Extracted from the pill's own handler on 2026-09-01 for a second caller: the guided walk's
	// "Yes, stop the tour" (onboarding_guide.js endTour). Taking the wall down is only half of
	// stopping a tour that put the reader on a session screen — the reader is still ON it, and
	// "stop the tour" means the plain Learn tab (owner call 2026-09-01). (A session hid the mode
	// tabs too until R15; it no longer does, but the plain tab is still where a stopped tour lands.)
	// Note it works on the RECAP too, where `session` is already null (finishSession clears it
	// before painting) but the chrome is still the session's: unconditional sessionChrome(false) is
	// what makes that case come out right.
	function backToTray() {
		session = null;
		meetUnflip = null;
		sessionChrome(false);
		showTray();
	}

	// quiet = true when the cards come FROM a server rehydrate (don't echo the save straight back);
	// a real add (nudge chip, "Add to Learning") persists the new tray so a refresh restores it.
	// replace = true when a BULK add supersedes the whole tray (All / Selected / Random from a Word
	// List) — the tray is wiped first so it becomes exactly the added words; the per-word individual
	// add leaves it false and appends. Accepts clusters (expanded to one card per sense via the shared
	// rule) or ready-made cards. Re-adding a partially ×-removed word restores its removed senses,
	// inserted next to their siblings so a word's cards stay adjacent.
	//
	// RETURNS the cards it actually staged, in tray order (empty when every one of them was already
	// there). main.js reads that to grow the rendered tray by exactly those rows instead of
	// re-rendering it, and to tell an add that changed nothing from one that did.
	function addWords(items, quiet, replace) {
		if (!items || !items.length) { return []; }
		if (replace) { tray = []; deletedSenseIds = {}; pendingPractice = null; }
		var have = {};
		var words = {}, wordCount = 0;   // distinct staged words — the tray cap counts these, not cards
		for (var i = 0; i < tray.length; i++) {
			have[cardKey(tray[i])] = true;
			var staged = tray[i] && tray[i].term && tray[i].term.id;
			if (staged != null && !words[staged]) { words[staged] = true; wordCount++; }
		}
		var added = [];
		for (var j = 0; j < items.length; j++) {
			var item = items[j];
			if (!item || !item.term || item.term.id == null) { continue; }
			// Cap backstop (learn.tray-cap → the tray_cap global): main.js truncates the add AND tells
			// the user; this only guarantees the tray itself stays within the cap whatever the caller
			// hands over. A word already staged always finishes — the ceiling is in words, not cards.
			if (!words[item.term.id]) {
				if (wordCount >= trayCap()) { continue; }
				words[item.term.id] = true; wordCount++;
			}
			var cards = (item.senseId !== undefined) ? [item]
				: (window.senseCardsOf ? window.senseCardsOf(item) : []);
			for (var k = 0; k < cards.length; k++) {
				var card = cards[k];
				if (have[cardKey(card)]) { continue; }
				have[cardKey(card)] = true;
				if (card.senseId != null) { delete deletedSenseIds[card.senseId]; }
				tray.splice(insertIndexFor(card.term.id), 0, card);
				added.push(card);
			}
		}
		if (added.length) { pendingPractice = null; }   // fresh words to learn — the tray is a learn tray again
		if (added.length && !quiet) { persistTray(); }
		return added;
	}

	// The tray ceiling in WORDS, from the tray_cap global (see main.js / TrayService).
	function trayCap() {
		var n = +window.tray_cap;
		return (n > 0) ? n : 100;
	}

	// After the word's last staged card, else the end of the tray.
	function insertIndexFor(termId) {
		for (var i = tray.length - 1; i >= 0; i--) {
			if (tray[i] && tray[i].term && tray[i].term.id === termId) { return i + 1; }
		}
		return tray.length;
	}

	// Rehydrate hands back the senses stored soft-deleted (senseId -> termId), so the next save
	// keeps them hidden even though they never passed through this session's removeWord.
	function absorbDeleted(map) {
		for (var k in map) { deletedSenseIds[k] = map[k]; }
	}

	function getDeletedSenseIds() {
		var out = [];
		for (var k in deletedSenseIds) { out.push(+k); }
		return out;
	}

	// Persist the current tray (server-side) via main.js, which owns the pair + the POST. No-op until
	// main.js has armed persistence. Never called from reset() — a pair change wipes the tray and the
	// NEW pair's stored copy must stay intact (it's rehydrated separately).
	function persistTray() {
		if (window.persistLearnTray) { window.persistLearnTray(); }
	}

	// Drop a single sense card from the tray (its entry's corner ×) and repaint in place — same
	// idea as the Clear button, just one row instead of all of them. The removed sense is
	// remembered (stored soft-deleted), so it stays hidden until its word is re-added. senseId is
	// null only for a legacy term-grained card (nothing to remember — it was never persisted).
	function removeWord(termId, senseId) {
		for (var i = 0; i < tray.length; i++) {
			var c = tray[i];
			if (!c || !c.term) { continue; }
			if (senseId != null ? c.senseId === senseId : (c.term.id === termId && c.senseId == null)) {
				// Term mode drops the whole word — don't soft-delete the main sense (a later
				// study-unit flip would otherwise hide it from an otherwise whole term).
				if (c.senseId != null && !termMode()) { deletedSenseIds[c.senseId] = c.term.id; }
				tray.splice(i, 1);
				persistTray();
				showTray();
				return;
			}
		}
	}

	// ── tray view ────────────────────────────────────────────────────────────────
	//
	// The tray shares Practice's rendering ENTIRELY: main.js paints its clusters as lean rows
	// (buildEntryElement) into the shared #defId box — same sound / add-to-list / IPA / rich-drill —
	// and this module only adds each word's learning-status badge (decorateTray) + the Start/Clear
	// band (renderCta). There is no bespoke tray-row markup here any more; #learnId (root) is used
	// solely for the rung-ladder session.

	// Hand the tray back to main.js to repaint as shared lean rows in #defId (it reads getTray() +
	// calls renderCta + decorateTray). Called by main.js's applyLearnChrome AND by this module's own
	// session-exit paths (back link / recap). A defensive fallback keeps the module standalone.
	function showTray() {
		if (window.endLearnDrill) { window.endLearnDrill(); }   // leaving the session ends any reveal drill
		disarmSpace();
		stopTermSound();
		session = null;
		if (window.renderLearnTray) { window.renderLearnTray(); }
		else { root().empty(); }   // main.js not wired (shouldn't happen) — at least clear the box
	}

	// Expose the staged clusters so main.js can render them through the Practice path.
	function getTray() { return tray; }

	// Paint each tray card's learning-status badge onto its shared lean row (matched by
	// data-sense-id): New / R0–R5 progress dots / Ready. Called by main.js's displayAll after
	// the rows are built. Only tray members are decorated — a dictionary page seeked mid-drill
	// won't get learn badges on its non-tray rows, because we iterate the tray, not whatever list
	// was rendered. Legacy cards without a sense get no badge (they have no ladder state).
	function decorateTray(defArea) {
		var ids = [];
		for (var i = 0; i < tray.length; i++) {
			var c = tray[i];
			if (!c || c.senseId == null) { continue; }
			// A per-sense row carries data-sense-id itself; in the combined tray view the row
			// spans a word and each SENSE BLOCK inside its def cell carries the id instead.
			var row = defArea.find("._entryClass[data-sense-id='" + c.senseId + "'], "
				+ "._senseBlockClass[data-sense-id='" + c.senseId + "']").first();
			if (!row.length) { continue; }
			ids.push(c.senseId);
			var cell = row.hasClass("_senseBlockClass") ? row : row.find("._defClass").first();
			if (cell.length) {
				// A combined block carries a ._cornerClass (badge sits leftmost in it, before ✓/×);
				// a single-sense card has none, so the badge goes into the def cell (CSS positions it).
				var holder = cell.find("._cornerClass").first();
				if (!holder.length) { holder = cell; }
				if (!holder.find(".lrnRowBadgeClass").length) {
					holder.prepend($("<span>").addClass("lrnRowBadgeClass"));
				}
			}
		}
		if (!ids.length) { return; }
		$.ajax("api/learning/sense-status", {
			type: "POST",
			data: { senseIds: ids },
			dataType: "json",
			traditional: true,
			success: function (data) {
				if (!data || !data.senses) { return; }
				for (var i = 0; i < data.senses.length; i++) {
					statusBySenseId[data.senses[i].senseId] = data.senses[i];
				}
				paintBadges(defArea);
				// Now that graduation status is known, the band may need to flip from "Start Learning!"
				// to the practice hero (e.g. an all-graduated tray restored after a page refresh, where
				// the in-memory pendingPractice was lost). renderCta no-ops during a live session.
				renderCta(tray.length > 0);
			},
			error: function () { /* badges are decoration — stay quiet */ }
		});
	}

	// The same progress dots on rendered WORD-LIST rows, keyed by TERM instead of by sense.
	//
	// decorateTray above iterates the TRAY, not whatever is on screen — which is exactly right for
	// tray cards and useless for a word list, whose rows are term-keyed and owe nothing to the tray.
	// So this twin walks the rendered rows and asks the term-grained endpoint about them: rung = the
	// max across the word's senses, "started" = something begun that is not just an "I know this"
	// ban. Only started-and-not-yet-graduated words get dots — a graduated word already shows its
	// germination shoot in the plant slot, and an untouched one has nothing to say.
	//
	// The dots go in the row's LEADING status column, where every other list shows the word-garden
	// plant and its day badge. On this band that column is empty by construction (an In-progress
	// word has no plant and no due date — that is what the section means), so buildRow reserves it
	// for the dots instead, at the same width, and this pass fills it. See .lrnRowDotsHostClass in
	// word-lists.css. Rows without the host (any other list) are left alone.
	// Decoration only: any failure stays quiet.
	// Rung statuses already fetched on this page, by term id (null = asked, nothing to draw). Same
	// reason as the plant cache in garden.js: these rows are rebuilt far more often than a rung
	// changes — every scroll page, every list reopen, every letter typed into a word filter — and a
	// round trip per rebuild made the dots flash off and back on under the user's hands.
	var rungCache = {};

	function paintRungDots(row, st) {
		var host = row.find(".lrnRowDotsHostClass");
		if (!host.length) { return; }
		host.empty();                            // idempotent: a repaint replaces, never stacks
		if (!st || !st.started || st.graduated) { return; }
		var dots = dotCluster(st.rung, false).addClass("lrnRowDotsClass");
		dots.assignTooltip("learnRow_progress_tooltip");
		host.append(dots);
	}

	// Two passes: what is already known paints synchronously, then the server is asked about ALL of
	// them and the rows repaint — so a rung that moved since it was last seen corrects itself one
	// round trip later, and repainting an unchanged one is invisible.
	function decorateTermRows(scope) {
		var $scope = scope && scope.jquery ? scope : $(scope);
		if (!$scope || !$scope.length) { return; }
		var $rows = $scope.find(".wlWordRowClass[data-term-id]");
		if (!$rows.length) { return; }
		var seen = {}, termIds = [];
		$rows.each(function () {
			var id = this.getAttribute("data-term-id");
			if (id && !seen[id]) { seen[id] = 1; termIds.push(id); }
			if (id && rungCache.hasOwnProperty(id)) { paintRungDots($(this), rungCache[id]); }
		});
		$.ajax("api/learning/term-status", {
			type: "POST",
			data: { termIds: termIds },
			dataType: "json",
			traditional: true,
			success: function (data) {
				if (!data || !data.terms) { return; }
				var byId = {};
				for (var i = 0; i < data.terms.length; i++) { byId[data.terms[i].termId] = data.terms[i]; }
				for (var j = 0; j < termIds.length; j++) {
					rungCache[termIds[j]] = byId[termIds[j]] || null;
				}
				$rows.each(function () {
					paintRungDots($(this), byId[this.getAttribute("data-term-id")]);
				});
			},
			error: function () { /* badges are decoration — stay quiet */ }
		});
	}

	function paintBadges(defArea) {
		for (var i = 0; i < tray.length; i++) {
			var c = tray[i];
			if (!c || c.senseId == null) { continue; }
			var badge = defArea.find("._entryClass[data-sense-id='" + c.senseId + "'] .lrnRowBadgeClass, "
				+ "._senseBlockClass[data-sense-id='" + c.senseId + "'] .lrnRowBadgeClass").empty();
			if (!badge.length) { continue; }
			var st = statusBySenseId[c.senseId];
			if (!st) { continue; }
			if (st.known) { continue; }   // a banned row is grayed with its ✓ on — no badge on top
			// "Ready" (graduated) and every planted stage now render as the garden plant in the row's
			// term-cell slot (GARDEN.decorate in main.js's displayAll), so the corner badge no longer
			// draws the old "Ready" text — it keeps only the pre-graduation learning states.
			if (st.graduated) { continue; }
			if (st.started) {
				var dots = dotCluster(st.rung, false);
				dots.assignTooltip("learnRow_progress_tooltip");
				badge.append(dots);
			} else if (!st.seenInDrill) {
				badge.append($("<span>").addClass("lrnBadgeNewClass").text(getRes("learnNew_badge")));
			}
		}
	}

	// Every usable tray card already graduated → the ready cards to hand Practice, else null. Mirrors
	// finishSession's readyCards (whole word in term mode) but sourced from the persisted per-sense
	// status (statusBySenseId), so it survives a page refresh where the in-memory pendingPractice is
	// gone. Returns null while any status is still unloaded or any card is still learning, so the band
	// falls back to Start Learning until the badges' status round-trip settles it.
	function graduatedReadyCards() {
		var out = [];
		for (var i = 0; i < tray.length; i++) {
			var c = tray[i];
			if (!c || c.senseId == null) { return null; }   // legacy card: no ladder status to read
			if (isKnown(c.senseId)) { continue; }            // banned-as-known: never taught, ignore
			var st = statusBySenseId[c.senseId];
			if (!st) { return null; }                        // status not back yet — unknown
			if (!st.graduated) { return null; }              // still learning → not all ready
			var sids = cardSenseIds(c);
			for (var s = 0; s < sids.length; s++) {
				out.push({ senseId: sids[s], termId: c.term && c.term.id });
			}
		}
		return out.length ? out : null;
	}

	// The tray's top control band — "Start Learning!" hero + Clear — rendered into the external
	// #learnControlBandId (a sibling of the Practice band, ABOVE the box) so the Learn tab reads the
	// same as Practice. Shown only while the tray is up; every session/recap screen hides it.
	function renderCta(enabled) {
		// Never paint the tray band over a LIVE session (a mid-session ban refreshes counts via
		// main.js → renderCta; the band belongs to the tray view only and returns with showTray).
		if (session) { return; }
		var band = ctaHost().empty();

		// Every teachable word already graduated — either fresh from a just-finished session
		// (pendingPractice, in memory) or restored after a refresh (persisted status). Both show the
		// SAME hero as the recap ("Go to the practice now!") instead of a dead "Start Learning!" that
		// would only re-open an empty session. Taking them to Practice moves the rows out; adding fresh
		// words to learn clears pendingPractice and re-enters the learning ladder, restoring Start.
		var readyCards = (pendingPractice && pendingPractice.length) ? pendingPractice : graduatedReadyCards();
		if (readyCards && readyCards.length) {
			var go = $("<button>").attr("type", "button").addClass("lrnStartButtonClass")
				.append($("<span>").addClass("lrnStartLabelClass").text(getRes("learnRecap_drillNow")))
				.click(function () { takePractice(readyCards); });
			band.append(go);
			// The words are all ready, but they stay learnable — a "Learn again" pill re-teaches them
			// (the ladder restarts at rung 0; graduation is untouched) for the user who deliberately
			// kept/re-added ready words to drill them once more, instead of only handing them to Practice.
			// It sits beside Clear in the bottom-right controls group, not stretched like the hero.
			var relearn = $("<button>").attr("type", "button").addClass("pl-btn-pill lrnRelearnButtonClass")
				.append($("<span>").addClass("pl-ic pl-ic-rotate-ccw"))
				.append($("<span>").text(getRes("learnRelearn_button")))
				.prop("disabled", !enabled);
			relearn.assignTooltip("learnRelearn_tooltip");
			if (enabled) { relearn.click(startSession); }
			band.append(clearControl(enabled).prepend(relearn));
			band.css("display", "flex");
			if (window.OB_TIPS) { OB_TIPS.evaluate(); }   // the handoff face — see below
			return;
		}

		// Banned-as-known cards sit in the tray grayed but won't be taught — count what a
		// session would actually offer, and don't offer to start when that's nothing.
		var usable = usableTrayCount();
		var startEnabled = enabled && usable > 0;
		var start = $("<button>").attr("type", "button").addClass("lrnStartButtonClass")
			.prop("disabled", !startEnabled);
		start.append($("<span>").addClass("lrnStartLabelClass").text(getRes("learnStartButton_label")));
		// Sub-label mirrors Practice's #startCountId: the teachable count, in the button's own ink.
		// In the combined view a polysemous tray reads clearer as "N words · M meanings" (N distinct
		// words, M teachable senses) — shown only when a word actually carries more than one meaning.
		var countText = getRes("startButton_wordsInExercise") + " " + usable;
		if (window.combined_tray_view && !termMode()) {   // term mode: the tray IS one card per word
			var seenTerms = {}, words = 0;
			for (var i = 0; i < tray.length; i++) {
				var c = tray[i];
				if (!c || (c.senseId != null && isKnown(c.senseId))) { continue; }
				var tk = (c.term && c.term.id != null) ? ("t" + c.term.id) : ("row" + i);
				if (!seenTerms[tk]) { seenTerms[tk] = true; words++; }
			}
			if (usable > words) { countText = strFormat(getRes("trayCount_wordsAndMeanings"), words, usable); }
		}
		start.append($("<span>").addClass("smallFont lrnStartCountClass").text(" (" + countText + ")"));
		start.assignTooltip("learnStartButton_tooltip");
		if (startEnabled) { start.click(startSession); }
		band.append(start);
		band.append(clearControl(enabled));
		band.css("display", "flex");
		// Beacon tips (spec §6): the explore-mode chain anchors on this band's hero —
		// re-evaluate whenever it is (re)built, in either face.
		if (window.OB_TIPS) { OB_TIPS.evaluate(); }
	}

	// The right-aligned Clear pill — shared by the Start hero and the "Go to the practice now!" hero.
	function clearControl(enabled) {
		var controls = $("<span>").addClass("lrnCtaControlsClass");
		var clear = $("<button>").attr("type", "button").addClass("pl-btn-quiet lrnClearButtonClass")
			.append($("<span>").addClass("pl-ic pl-ic-trash"))
			// Icon-only, to match the Practice tab's Clear (which shed its label when the review
			// button joined that band). The word survives as the accessible name; the tooltip
			// already carries the explanation.
			.attr("aria-label", getRes("clearTray_button"))
			.prop("disabled", !enabled);
		clear.assignTooltip("learnClear_tooltip");
		if (enabled) { clear.click(function () { clearTray(); showTray(); }); }
		controls.append(clear);
		return controls;
	}

	// Hand the graduated cards to Practice (adds them to the Practice tray + switches to it). The words
	// MOVE: they're dropped from the learn tray here (so the learn tray is clean next visit) and the
	// pending-practice hero is cleared. NOT a ×-remove — nothing is remembered as removed; a graduated
	// word's state lives in its learning row, and re-adding it later brings it back normally.
	function takePractice(cards) {
		var go = (cards || pendingPractice || []).slice();
		pendingPractice = null;
		if (go.length) {
			var gone = {};
			for (var i = 0; i < go.length; i++) { if (go[i].senseId != null) { gone[go[i].senseId] = true; } }
			var rest = [];
			for (var t = 0; t < tray.length; t++) {
				var sids = cardSenseIds(tray[t]), moved = false;
				for (var s = 0; s < sids.length; s++) { if (gone[sids[s]]) { moved = true; break; } }
				if (!moved) { rest.push(tray[t]); }
			}
			if (rest.length !== tray.length) { tray = rest; persistTray(); }
		}
		if (window.learnHandoffToPractice) { window.learnHandoffToPractice(go); }
	}

	// The guided tour's PRACTICE DOOR (onboarding_guide.js, owner rulings R16 + R21,
	// docs/project/first-run-redesign.md): the reader pressed the Practice tab in the middle of the
	// Learn chapter and said yes — "if user goes to Practice early, the two words go to practice".
	// The recap's button is the only other hand-off there is, and mid-ladder nothing has graduated,
	// so without this the reader would land on an empty Practice tab.
	//
	// WHICH words: the live session's (an "I know this" ban excepted, exactly as the recap skips
	// it), else the words the recap or the tray hero is already offering (pendingPractice), else the
	// tray's — the door is open on the Start screen too, before the first card.
	//
	// The Learn tray KEEPS every word that has not graduated: it is still being learned, its rung is
	// stored per event, and the next Learn session resumes it where it stood. A graduated word moves
	// out the way takePractice moves it at the recap. Returns false when there was nothing to take
	// (every word banned), so the caller can still open the tab the reader asked for.
	function toPractice() {
		var go = [], moved = {};
		function take(card, termId, graduated) {
			var sids = cardSenseIds(card);
			for (var s = 0; s < sids.length; s++) {
				if (isKnown(sids[s])) { continue; }
				go.push({ senseId: sids[s], termId: termId });
				if (graduated) { moved[sids[s]] = true; }
			}
		}
		if (session) {
			for (var i = 0; i < session.words.length; i++) {
				var word = session.words[i];
				if (!word.known) { take(word.w, word.w.termId, word.done); }
			}
		} else if (pendingPractice) {
			go = pendingPractice.slice();
			for (var p = 0; p < go.length; p++) { moved[go[p].senseId] = true; }
		} else {
			for (var t = 0; t < tray.length; t++) {
				if (tray[t] && tray[t].term) { take(tray[t], tray[t].term.id, false); }
			}
		}
		if (!go.length) { return false; }
		var rest = [];
		for (var r = 0; r < tray.length; r++) {
			var rs = cardSenseIds(tray[r]), out = false;
			for (var q = 0; q < rs.length; q++) { if (moved[rs[q]]) { out = true; break; } }
			if (!out) { rest.push(tray[r]); }
		}
		pendingPractice = null;
		if (rest.length !== tray.length) { tray = rest; persistTray(); }
		// main.js addSensesToPractice → loadWordListSelection → randomWordsView, whose LRN.leave()
		// drops the session and hands its chrome back: the same road the recap's button takes.
		if (window.learnHandoffToPractice) { window.learnHandoffToPractice(go); }
		return true;
	}

	// Hide the top control band — a session/recap screen is not the tray (the band would offer
	// "Start Learning!" over a running session). Reappears when showTray/renderCta run again.
	function hideCta() {
		ctaHost().hide().empty();
		// Beacon tips: a session/recap screen just took the stage — clear idle beacons.
		if (window.OB_TIPS) { OB_TIPS.evaluate(); }
	}

	// `count` = how many rungs the ladder asks: the session's own (5 on the guided first run, R10),
	// RUNG_COUNT for a tray badge or a word-list row, which read the stored ladder.
	function dotCluster(fill, current, count) {
		var c = $("<span>").addClass("lrnDotsClass" + (current ? " lrnDotsCurrentClass" : ""));
		var n = count || RUNG_COUNT;
		for (var i = 0; i < n; i++) {
			c.append($("<span>").addClass("lrnDotClass" + (i < fill ? " lrnDotOnClass" : "")));
		}
		return c;
	}

	// ── the session ──────────────────────────────────────────────────────────────

	// Sense mode: one ladder word per returned card. An already-graduated sense the user chose to keep
	// in (or re-add to) the learning tray is RE-TAUGHT — its ladder restarts at rung 0. Graduation is a
	// permanent milestone server-side (graduated_ts is never cleared), so a re-learn leaves the "ready"
	// badge intact; it just lets the user drill the word again when they want to.
	function buildSenseWords(cards) {
		var open = [];
		for (var i = 0; i < cards.length; i++) {
			var rung = cards[i].graduated ? 0 : (cards[i].rung || 0);
			open.push({ w: cards[i], rung: rung, fails: 0, lastStep: -9, fastlane: false, done: false });
		}
		return open;
	}

	// Term mode: ONE ladder word per WORD. Collapse the returned per-sense cards by term — the main
	// (first) sense stands for the word (its article/IPA/distractor pools drive the rungs), the
	// composed gloss (carried on the tray card) is the meaning shown on every MC rung, and w.senseIds
	// lists every sense so a pass/miss/graduation writes to them all in lockstep. Start rung = the
	// least-learned sense's (they advance together after this); a wholly-graduated word is dropped.
	function buildTermWords(cards) {
		var glossByTerm = {};
		for (var t = 0; t < tray.length; t++) {
			if (tray[t] && tray[t].term && tray[t].term.id != null) { glossByTerm[tray[t].term.id] = tray[t].gloss; }
		}
		var byTerm = {}, order = [];
		for (var i = 0; i < cards.length; i++) {
			var tid = cards[i].termId;
			if (!byTerm[tid]) { byTerm[tid] = []; order.push(tid); }
			byTerm[tid].push(cards[i]);
		}
		var open = [];
		for (var o = 0; o < order.length; o++) {
			var group = byTerm[order[o]];
			var allGraduated = true, minRung = RUNG_COUNT, senseIds = [];
			for (var g = 0; g < group.length; g++) {
				senseIds.push(group[g].senseId);
				if (!group[g].graduated) { allGraduated = false; }
				var rg = group[g].rung || 0;
				if (rg < minRung) { minRung = rg; }
			}
			var w = {};
			for (var k in group[0]) { w[k] = group[0][k]; }   // main sense = the representative card
			w.senseIds = senseIds;
			w.gloss = glossByTerm[w.termId] != null ? glossByTerm[w.termId] : w.gloss;   // composed session gloss
			// A wholly-graduated word the user kept in the tray is RE-TAUGHT from rung 0 (see
			// buildSenseWords) — graduation stays a permanent milestone, this just re-opens the ladder.
			open.push({ w: w, rung: allGraduated ? 0 : minRung, fails: 0, lastStep: -9, fastlane: false, done: false });
		}
		return open;
	}

	// ── the session's ladder (R10) ───────────────────────────────────────────────
	//
	// Is this the guided tour's first Learn session? The walk's own answer (OB_GUIDE.firstRun:
	// chapter 1 escorting, and no Learn session finished since it was armed — the same signal the
	// first scoop's size has always read). Asked ONCE, at Start: the ladder a session climbs never
	// changes under it, even if the tour ends half way up.
	function guidedFirstRun() {
		return !!(window.OB_GUIDE && OB_GUIDE.firstRun && OB_GUIDE.firstRun());
	}

	function ladder() { return (session && session.rungs) || FULL_RUNGS; }

	// The rung a word standing at `r` is actually ASKED at: `r` itself, or the next one up that this
	// session's ladder keeps. A skipped rung is passed over HERE, at the moment of asking, exactly as
	// the look-alike rung has always been skipped on a word with too few look-alikes — never by a
	// recorded pass. The server hears only about the rungs that were asked (rungAsked), and the
	// ladder state it keeps reads "R2 next" after an R1 pass until the R4 pass moves it on, so an
	// ORDINARY session that later resumes the word still asks R2 and R3 (R10: "ordinary sessions keep
	// all 7"). The SRS and the garden never see a rung at all — they start at the drill.
	function keptRung(r) {
		var rungs = ladder();
		for (var i = 0; i < rungs.length; i++) { if (rungs[i] >= r) { return rungs[i]; } }
		return r;
	}

	// A miss drops ONE rung of this session's ladder, never below R1: on the guided ladder a miss on
	// the look-alikes (R3) goes back to picking the meaning (R1) — the rung below it that is
	// actually there — and not to a skipped rung that keptRung would push straight back up to R3.
	function rungBelow(r) {
		var rungs = ladder(), below = 1;
		for (var i = 0; i < rungs.length; i++) { if (rungs[i] >= 1 && rungs[i] < r) { below = rungs[i]; } }
		return below;
	}

	// A word's filled dots: how many of this session's rungs lie below where it stands. Equals the
	// rung itself on the full ladder, so the seven-dot clusters read exactly as before.
	function rungsPassed(r) {
		var rungs = ladder(), n = 0;
		for (var i = 0; i < rungs.length; i++) { if (rungs[i] < r) { n++; } }
		return n;
	}

	// Start is a TWO-PASS load, for the same reason the finish screen is: the first card of a session
	// is a meet card, and a meet card shows no wrong answers — but building the wrong answers is ~95%
	// of what the server does for a batch (a rank-band scan per term, ~275ms each, whenever the
	// precomputed store has nothing for the pair). Waiting for them put 1–2 seconds between the click
	// and the first word. So both passes are fired AT ONCE: the paint pass comes back in ~100ms and
	// the session starts on it, while the options pass lands during the first card's reading time and
	// is merged in behind the learner's back.
	function startSession() {
		var ids = traySenseIds();
		if (!ids.length || session) { return; }
		// A restart has begun (connection.js): a session started now would be cut off at once.
		if (PlConn.blocksNewExercise()) {
			showMessage(getRes("connNoNewExercise"));
			return;
		}
		pendingPractice = null;   // starting a new session supersedes any prior finish's practice invite
		pendingOptions = null;    // …and so does any options payload a FAILED earlier start left behind
		var params = { termLang: langToCode(), defLang: langFromCode(), senseIds: ids };
		// Fired FIRST so it is already on the wire while the paint pass is being served — the two are
		// independent, and this is the one whose latency we are hiding. Its failure must never strand
		// a session at the wait strip, so it opens the gate on an empty set instead of raising: the
		// choice rungs already cope with a pool that came back short (deal() of nothing is nothing).
		ajaxPost("api/learning/sense-batch-options", params, applyOptions, null,
			function () { applyOptions({ options: {} }); });
		// Groups this run's rows in the answer history (user_answer_event) — minted BEFORE the start
		// call, so the server can key the session's LEARN_START row to the same id
		// (docs/project/dashboard-users-engagement-plan.md §2a).
		var sid = "L" + new Date().getTime().toString(36) + Math.floor(Math.random() * 1e6).toString(36);
		ajaxPost("api/learning/sense-batch-start", $.extend({ clientSessionId: sid }, params),
			function (data) {
				var cards = (data && data.cards) || [];
			foldAnswers = !!(data && data.foldAnswers);   // language rule for this session's term lang
				var open = termMode() ? buildTermWords(cards) : buildSenseWords(cards);
				if (!open.length) {
					// Nothing teachable — every tray sense is banned-as-known or missing (graduated
					// senses are re-taught now, so they no longer empty a session). Just repaint the
					// tray in place; the band's own enable/relearn logic handles what's offered.
					showTray();
					return;
				}
				var guided = guidedFirstRun();
				session = {
					words: open, step: 0, cur: null, t0: new Date().getTime(),
					seq: 0,   // client-side event ordinal (0042) — the true interaction order
					// The rungs this session asks (R10): five on the tour's first session, seven on
					// every other. `guided` also puts the label in front of the dots (R22).
					rungs: guided ? GUIDED_RUNGS : FULL_RUNGS, guided: guided,
					r3Cards: 0,   // R3 cards asked this run — gates the "none of these" card (§7)
					// Pass 2's landing state. A card above rung 0 cannot be drawn until this is true.
					optionsReady: false, optionsWaiters: [],
					sid: sid
				};
				if (pendingOptions) { applyOptions(pendingOptions); }   // pass 2 won the race
				prefetchSessionSounds(open);        // warm the URL cache for every word before the ladder needs it
				prefetchSessionRichEntries(open);   // …and the rich entries the meet cards are going to reveal
				hideCta();             // leaving the tray for a live session
				sessionChrome(true);   // the session box, the panel's adds off — the tabs stay (R15)
				bindSpaceKey();        // wire the Enter / 1-9 shortcuts (once) now that a session is live
				next();
			},
			getRes("learnError_message"));
	}

	// Pass 2's payload when it beat pass 1 home — the session object it has to be merged into does not
	// exist yet at that point. Cleared as soon as it is applied, so it can never leak into the NEXT
	// session (whose own pass 2 overwrites it anyway).
	var pendingOptions = null;

	// Merge the wrong answers onto the live cards, by sense. In term mode a ladder word carries a
	// shallow copy of its main sense's card, so its own senseId is still the right key. Anything the
	// server skipped simply has no entry and keeps whatever it had — i.e. nothing, and renderChoice's
	// existing thin-pool handling covers it, exactly as it did when a batch came back short.
	function applyOptions(data) {
		var options = data && data.options;
		if (!options) { return; }
		if (!session) { pendingOptions = data; return; }
		pendingOptions = null;
		for (var i = 0; i < session.words.length; i++) {
			var w = session.words[i].w;
			var got = w && w.senseId != null ? options[String(w.senseId)] : null;
			if (got) { for (var k in got) { w[k] = got[k]; } }
		}
		session.optionsReady = true;
		var waiting = session.optionsWaiters;
		session.optionsWaiters = [];
		for (var j = 0; j < waiting.length; j++) { waiting[j](); }
	}

	// How many steps a card stays out of the draw after being shown
	// (docs/design/learn-distractor-quality.md §8). One step of spacing — the old rule — meant a
	// miss-demoted word could come back after a single intervening card, which is not "it recedes",
	// it is asking the same question again. Relaxed one step at a time when the rung's pool cannot
	// fill: the window is a preference, and a session with three words left still has to deal them.
	var SPACING_STEPS = 3;

	// The verb a card studies, or null for anything that is not a verb form. Both verb card types
	// carry it, filled the same way on the server (LearningService): a form-in-use combo gets the
	// lemma of the paradigm cell it resolved to ("ich brenne ab" → "abbrennen"), a form-set card
	// gets term.lemma ("fahren, fährt, fuhr, ist gefahren" → "fahren"). In practice the lemma gap
	// below is a rule about form-in-use cards, because a verb has exactly ONE form-set card: it can
	// collide with its verb's combos, never with a sibling of its own kind. Nothing else in the
	// session carries verbLemma, so a plain word neither blocks nor is blocked.
	function cardLemma(c) { return c && c.w && c.w.verbLemma ? c.w.verbLemma : null; }

	// Scheduler: draw from ALL not-done cards (no rolling window), lowest rung first, shuffled within a
	// rung, and not one seen in the last SPACING_STEPS — so every sense clears rung N before anything
	// advances to rung N+1 (a pass bumps a card up, but it won't reappear until its rung-peers are
	// done too).
	//
	// The spacing window OUTRANKS the rung order, and that is the whole point rather than a detail.
	// A miss demotes the word one rung, which usually leaves it ALONE on that rung — so a scheduler
	// that always serves the lowest rung first hands the very card you just got wrong straight back,
	// which is the case the window exists for (owner report 2026-07-31). Rungs are therefore tried
	// lowest first but SKIPPED when everything on them is still inside the window; the next rung up
	// supplies a card instead, and the missed word comes back a few cards later.
	function pickNext() {
		var pending = [];
		for (var i = 0; i < session.words.length; i++) {
			if (!session.words[i].done) { pending.push(session.words[i]); }
		}
		if (!pending.length) { return null; }

		var rungs = [];
		for (var j = 0; j < pending.length; j++) {
			if (rungs.indexOf(pending[j].rung) < 0) { rungs.push(pending[j].rung); }
		}
		rungs.sort(function (a, b) { return a - b; });

		// When each verb was last put in front of the learner, over the WHOLE session — done cards
		// included, and rung-0 meets counted: a sighting is a sighting, and the meet is the
		// strongest one there is, because it reveals the answer outright. Keys carry a "#" prefix so
		// a lemma that happens to spell an Object.prototype member ("constructor") cannot read as
		// already seen.
		var lemmaSeen = {};
		for (var m = 0; m < session.words.length; m++) {
			var lm = cardLemma(session.words[m]);
			if (!lm) { continue; }
			var lk = "#" + lm;
			if (lemmaSeen[lk] == null || session.words[m].lastStep > lemmaSeen[lk]) {
				lemmaSeen[lk] = session.words[m].lastStep;
			}
		}

		// Study together, test apart (docs/project/verb-card-types.md, "Session order"). Is this card
		// far enough from the last showing of ANY card of its verb? Blocked testing lets the block be
		// the answer: having just seen `ich fahre` and `du fährst`, `er fährt` is inferable and
		// measures nothing.
		//
		// Rung 0 is exempt BY DESIGN — a verb's forms may be introduced together, because seeing a
		// paradigm as a shape is how it is taught. What "test apart" forbids is being ASKED, so the
		// rule starts at rung 1. `gap` 0 turns it off entirely.
		//
		// The card's OWN last showing is in lemmaSeen and can never bind: the caller has already
		// required `step - lastStep > n`, and `gap` never exceeds `n`.
		function farFromLemma(c, gap) {
			if (gap <= 0 || c.rung === 0) { return true; }
			var lm2 = cardLemma(c);
			if (!lm2) { return true; }
			return session.step - lemmaSeen["#" + lm2] > gap;
		}

		// Window OUTSIDE, rungs inside: the full window is tried across EVERY rung before it is
		// narrowed by a step. Nesting these the other way round narrows the window as soon as one
		// rung cannot fill it, which let a missed word — alone on its demoted rung — back after a
		// single intervening card. Rung order is the tie-break within a window, not above it.
		//
		// The lemma gap sits BETWEEN the two, and that placement is the whole of it. It is tried at
		// its widest and relaxed to nothing INSIDE each window level, so it can never push the repeat
		// window narrower than it would have gone on its own — handing back the very card just shown
		// is worse than showing another form of the same verb, so the lemma rule is the one that
		// yields. At gap 0 this body is byte-for-byte what it was before the rule existed, which is
		// why a tray of eight cells of ONE verb still deals, still in rung order, and cannot stall.
		for (var n = SPACING_STEPS; n >= 1; n--) {
			for (var g = n; g >= 0; g--) {
				for (var r = 0; r < rungs.length; r++) {
					var pool = [];
					for (var k = 0; k < pending.length; k++) {
						if (pending[k].rung === rungs[r]
								&& session.step - pending[k].lastStep > n
								&& farFromLemma(pending[k], g)) {
							pool.push(pending[k]);
						}
					}
					if (pool.length) { return pool[Math.floor(Math.random() * pool.length)]; }
				}
			}
		}
		// Every card in the session is inside even a one-step window: the card just shown is the
		// only one left. An immediate repeat is all there is.
		return pending[Math.floor(Math.random() * pending.length)];
	}

	function next() {
		if (!session) { return; }
		var word = pickNext();
		if (!word) { finishSession(); return; }
		session.cur = word;
		word.lastStep = session.step;
		if (word.rung === 0) {
			// The meet is per-TERM: one card covering every not-yet-met sense of this term (all
			// revealed at once), so a polysemous word isn't shown to "guess" once per sense.
			var termCards = [];
			for (var i = 0; i < session.words.length; i++) {
				var c = session.words[i];
				if (!c.done && c.rung === 0 && c.w.termId === word.w.termId) { termCards.push(c); }
			}
			renderMeet(termCards);
			return;
		}
		// Every rung above the meet asks a question that has wrong answers in it, and those ride
		// pass 2 (see startSession). A FRESH session never lands here before they arrive — its
		// opening cards are all meets, which is the whole reason the split is safe. Only a RESUMED
		// session, every word already past rung 0, can outrun them, and it then waits exactly as
		// long as the old single-call Start made every session wait.
		if (!session.optionsReady) {
			renderOptionsWait();
			session.optionsWaiters.push(function () {
				if (session && session.cur === word) { renderRung(word); }
			});
			return;
		}
		renderRung(word);
	}

	// The question rungs, once their options are in hand.
	function renderRung(word) {
		if (!session) { return; }
		word.compRung = false;   // set by the one rung that shows the term WITHOUT its pronoun
		// A rung this session's ladder does not ask is passed over here, before anything is asked or
		// marked (R10, keptRung): the tour's first session goes R1 → R3.
		word.rung = keptRung(word.rung);
		// The look-alike rung needs data; phrases (and thin corpora) skip it gracefully.
		if (word.rung === 3 && (!word.w.r3 || word.w.r3.length < 3)) { word.rung = 4; }
		// ── the two MEANING-choice rungs on a form-in-use card ──────────────────────────────────
		//
		// R1 (show the form, pick its meaning) and R5 (hear it, pick its meaning) both deal one
		// meaning per paradigm cell — the card's own gloss against its siblings'. Since 2026-08-27 a
		// cell HAS no meaning of its own: every cell of `abbrennen` means abbrennen, and the
		// per-cell translations those pools were built from are precisely the invented ones the
		// redesign removed (design §3). Asked today, both rungs offer four spellings of one meaning
		// and grade one of them right.
		//
		// R1 has a real question to put in their place — what the form SAYS, who and when — and
		// takes it wherever the card can carry it (hasComprehension names every condition).
		//
		// R5 does not, and is SKIPPED on these cards. Its cue is the clip, and the clip recites the
		// subject pronoun, so the who-and-when question is answered out loud by the cue itself. What
		// could replace it — hear the form, pick it out of its own siblings — is a new rung and a new
		// design, not this step's call. A card that also cannot carry R1 goes straight to production.
		if (word.rung === 1 && isVerbFormCard(word.w)) {
			if (hasComprehension(word.w)) {
				word.compRung = true;
				if (window.VISIT_LOG) { VISIT_LOG.mark("rung", { i: session.words.indexOf(word), rung: 1, comp: 1 }); }
				renderComprehension(word);
				return;
			}
			word.rung = keptRung(2);   // R2 — or, on the guided ladder, the next rung it keeps
		}
		if (word.rung === 5 && isVerbFormCard(word.w)) { word.rung = 6; }
		if (window.VISIT_LOG) { VISIT_LOG.mark("rung", { i: session.words.indexOf(word), rung: word.rung }); }
		// R4 on a form-set card asks for the changing part instead of the first letters, but only
		// where there IS one: an invariant verb ("cut, cut, cut") carries no changeGaps and keeps
		// the ordinary rung rather than being handed a question with no answer in it.
		if (word.rung === 4) {
			if (word.w.changeGaps && word.w.changeGaps.length) { renderChangeParts(word); }
			else { renderType(word); }
		}
		else if (word.rung === 5) { renderSound(word); }
		else if (word.rung === 6) { renderFullType(word); }
		else { renderChoice(word); }
	}

	// The stand-in shown while pass 2 is still out — the app-wide "Looking up…" strip (rich-loading,
	// from the dictionary's lean rows), not a second spinner of its own.
	function renderOptionsWait() {
		var body = sessionScreen(session.cur);
		body.addClass("lrnBodyTopClass");
		body.append($("<div>").addClass("rich-loading")
			.append($("<div>").addClass("rich-loading-text")
				.text(getRes("rich_loading_message") || "Looking up…"))
			.append($("<div>").addClass("rich-loading-bar")
				.append($("<div>").addClass("rich-loading-bar-fill"))));
	}

	function advance() {
		if (!session) { return; }
		session.step++;
		next();
	}

	// Stamp what the question being shown ASKS, for the answer history: which way it asked
	// (word→meaning or meaning→word), the cue text, the rung it was asked at, and when — so the
	// event posted on answer can carry rungAsked / cueShown / latency along with the outcome.
	function markAsked(word, direction, cue) {
		word.dir = direction;
		word.cue = cue;
		word.rungAsked = word.rung;
		word.askedAt = new Date().getTime();
		// 0042 per-asking instrumentation: one presentation id per SHOWING (all lockstep sense
		// rows share it), the answer-COMMIT stamp (answeredAt — the latency anchor, set by the
		// handler that decides pass/fail, NOT the Continue click), first-key latency on the
		// typing rungs, the per-asking try counter and the MC option count.
		word.pid = Util.uuid();
		word.answeredAt = null;
		word.firstKeyMs = null;
		word.attempts = null;
		word.optionsCount = null;
		word.noneTaught = false;   // R3 re-decides this per asking (§7)
		word.autoPlayed = false;
	}

	// First keydown in a typing rung's input → first_key_ms (the retrieval-latency signal).
	// Pure modifier presses don't count, same rule as the drill.
	function trackFirstKey(input, word) {
		input.on("keydown", function (e) {
			var kc = e.which || e.keyCode;
			if (kc === 16 || kc === 17 || kc === 18 || kc === 20 || kc === 91 || kc === 92) { return; }
			if (word.firstKeyMs == null && word.askedAt) {
				word.firstKeyMs = new Date().getTime() - word.askedAt;
			}
		});
	}

	// One ladder/history event per interaction, ONE request. kind = meet | pass | fail move the ladder
	// server-side; reveal is history-only. In term mode a word carries every sense (word.w.senseIds),
	// and the server writes the event to each in one transaction — the whole word advances (and
	// graduates) in lockstep. word.lastAnswer (the picked option / typed text) is consumed once.
	function postEvent(word, kind) {
		var senseIds = cardSenseIds(word.w);
		// 0042 fix: latency anchors at the answer COMMIT (answeredAt) — the Continue click that
		// posts this event used to leak the post-answer dwell into the number.
		var committed = word.answeredAt || new Date().getTime();
		var latency = word.askedAt ? (committed - word.askedAt) : null;
		var now = new Date();
		var seq = session ? ++session.seq : null;   // once per interaction — lockstep rows share it
		if (senseIds.length) {
			var data = {
				senseIds: senseIds,
				rung: word.rung,
				kind: kind,
				graduated: word.rung >= RUNG_COUNT,
				direction: word.dir,
				rungAsked: word.rungAsked,
				cueShown: word.cue,
				latencyMs: latency,
				answerText: word.lastAnswer,
				clientSessionId: session ? session.sid : null,
				presentationId: word.pid,
				seq: seq,
				firstKeyMs: word.firstKeyMs,
				attempt: word.attempts,        // R4 tries / R6 slips+1 / 1 on an MC pick
				optionsCount: word.optionsCount,
				soundOnlyCue: word.rungAsked === 5,   // R5: the spoken word IS the cue
				autoSoundPlayed: !!word.autoPlayed,
				termMode: termMode(),
				localHour: now.getHours(),
				localDow: now.getDay()
			};
			for (var k in data) { if (data[k] == null) { delete data[k]; } }
			// Kept and sent again until the server has it (connection.js): the ladder's state is
			// client-driven, but these rows are the word's whole Learn history.
			PlConn.post("api/learning/sense-event", data);
		}
		word.lastAnswer = null;
	}

	function passWord(word) {
		word.fails = 0;
		if (word.fastlane || word.rung >= RUNG_COUNT - 1) {
			word.rung = RUNG_COUNT;
			word.done = true;
			postEvent(word, "pass");
			toast(strFormat(getRes("learnReady_toast"), displayTerm(word.w)), "sprout");
		} else {
			word.rung++;
			postEvent(word, "pass");
		}
		advance();
	}

	// A miss drops one rung of the session's ladder (never below R1 — rungBelow); a second consecutive
	// miss re-opens the meet card.
	function failWord(word) {
		word.fails++;
		if (word.fastlane) {
			word.fastlane = false;
			word.rung = 1;
			word.fails = 0;
		} else if (word.fails >= 2) {
			word.rung = 0;
			word.fails = 0;
		} else {
			word.rung = rungBelow(word.rung);
		}
		postEvent(word, "fail");
		advance();
	}

	// ── keyboard: Enter = the screen's primary action, 1-9 = pick that option ─────
	// Each session screen registers its forward button here; ENTER fires it — except while a text
	// input is focused (the type rung) so it never eats keystrokes. Cleared between screens by
	// sessionScreen, and on leaving the session. The button also gets an "Enter" shortcut tooltip.
	// On a multiple-choice rung the options carry number badges (1, 2, …) and pressing that digit
	// clicks the matching option — so the hands never have to leave the keyboard (the mouse still works).
	var spaceAction = null;
	var curOptions = null;   // the current MC rung's option buttons, in displayed order (null off-rung)
	// Is a rich view the thing currently on screen (the flipped meet card)? Escape is the gesture that
	// dismisses a rich view everywhere else in the app, so on this one screen it stands in for the
	// forward action instead of being a dead key — see the Escape branch in bindSpaceKey.
	var richRevealOpen = false;
	var meetUnflip = null;   // the flipped meet card's way back to its front face (null = none on screen)
	var spaceKeyBound = false;
	function armSpace(fn) { spaceAction = fn; }
	function disarmSpace() { spaceAction = null; }
	function spaceTip(btn) { return btn.assignTooltip("learnSpace_tooltip"); }
	// Bound once, lazily, when a session first starts. (A plain top-level $(document).on in this IIFE
	// did not register at load — so bind natively, on demand, from startSession where it is guaranteed
	// to run.) Skipped entirely while a text field is focused (the type rung types its own letters).
	function bindSpaceKey() {
		if (spaceKeyBound) { return; }
		spaceKeyBound = true;
		document.addEventListener("keydown", function (e) {
			if (!session) { return; }
			var t = e.target, tag = (t && t.tagName || "").toLowerCase();
			var typing = (tag === "input" || tag === "textarea" || (t && t.isContentEditable));
			// Space → pronounce the word on screen — the app-wide sound shortcut, same as the Dictionary
			// tab. Works by clicking whatever sound icon the current screen shows, so it plays exactly
			// that word and does nothing on a screen without one. A visible rich reveal is main.js's
			// job — it fires first and preventDefaults, so we bow out to avoid a double play. Bare
			// Space is only ours when nothing else has a claim on the key: not while typing, and not on
			// a focused button (an MC rung's option buttons keep Space as their click key).
			if (e.keyCode === 32 || e.key === " ") {
				var held = e.ctrlKey || e.shiftKey || e.altKey || e.metaKey;
				var mine = !held && !typing && tag !== "button"
					&& !(t && t.getAttribute && t.getAttribute("role") === "button");
				if (mine && !e.defaultPrevented) {
					// Holding Space must not machine-gun the audio — swallow repeats, keep the scroll off.
					if (e.repeat) { e.preventDefault(); return; }
					var snd = root().find(".lrnSndLgClass, .lrnSndClass").filter(":visible").first();
					if (snd.length) { e.preventDefault(); snd.click(); }
				}
				return;   // Space is never Enter or a digit — nothing else to do here
			}
			if (typing) { return; }
			// Escape → the SAME forward action as Enter, but only while the flipped meet card's rich
			// view is what's on screen. A rich view is dismissed with Escape everywhere else in the
			// app, so a learner reading this one reaches for Escape and, until now, got nothing
			// (owner report 2026-07-31). Two things it deliberately does NOT do:
			//   • drill levels come first — main.js owns Escape while a drill-in is open, unwinds one
			//     word per press and marks the event handled, so this fires only once the base rich
			//     view is back on screen. Hence the defaultPrevented bail: main.js's handler is a
			//     jQuery one on the same document node, and its stopPropagation cannot stop a native
			//     listener registered here, only preventDefault tells us it acted.
			//   • it stays off every screen that is NOT a rich view (a rung, a miss strip), where
			//     Escape has nothing to dismiss and advancing on it would be a surprise.
			//   • and it stands down while the guided tour is reading this very card with the
			//     learner (OB_GUIDE.holdsEscape — the entry chapter, onboarding_guide.js). Its
			//     popups are Esc-dismissable like every other, so one press would put a step away
			//     AND skip the word underneath it, taking the rest of the chapter with it. The
			//     chapter hands the key back on its last step, whose copy is what promises Esc
			//     moves the card on.
			if (e.keyCode === 27 || e.key === "Escape") {
				if (!e.defaultPrevented && richRevealOpen && spaceAction
						&& !(window.OB_GUIDE && OB_GUIDE.holdsEscape())) {
					e.preventDefault();
					spaceAction();
				}
				return;
			}
			// Enter → the current screen's primary forward action (reveal → Got it → Continue).
			if ((e.keyCode === 13 || e.key === "Enter") && spaceAction) {
				e.preventDefault();
				spaceAction();
				return;
			}
			// 1-9 → pick that numbered option on a multiple-choice rung.
			if (curOptions && curOptions.length) {
				var n = (e.key >= "1" && e.key <= "9") ? parseInt(e.key, 10)
					: (e.keyCode >= 49 && e.keyCode <= 57) ? e.keyCode - 48 : 0;
				if (n >= 1 && n <= curOptions.length) {
					e.preventDefault();
					curOptions[n - 1].click();
				}
			}
		});
	}

	// ── uniform top-right forward action ─────────────────────────────────────────
	// Every session screen's advance button (meet "Got it →", a miss "Continue →") lands in the
	// SAME spot: the top strip's right edge, next to the progress dots. One placement, one look —
	// so the forward gesture never moves between screens. Enter fires whatever's parked there.

	function topActionHost() { return root().find(".lrnTopActionClass").first(); }

	function setTopAction(label, fn) {
		var host = topActionHost();
		// children(), not empty() — the sizing ghost stays. The sweep used to spare .fbCornerClass
		// because the feedback 💬 shared this slot; it rides the ladder line now, so the forward
		// action is the only button that can be here.
		host.children("button").remove();
		var btn = $("<button>").attr("type", "button").addClass("lrnCtaButtonClass lrnTopActionBtnClass")
			.text(label).click(fn);
		spaceTip(btn);
		host.append(btn);
		armSpace(fn);
		return btn;
	}

	// The forward-action slot, pre-sized to the button that will land in it. The button only appears
	// once the user answers (or flips the meet card), and letting it size the slot at that moment
	// steals width from the dot clusters sharing its line, sliding them sideways. The ghost carries
	// BOTH labels this slot can ever show, so the slot is as wide as the wider one measured — not as
	// wide as a guessed em value that a longer translation could outgrow. The two labels are stacked
	// in one grid cell by the CSS, so the ghost is one button tall, not two.
	function topActionSlot() {
		var slot = $("<div>").addClass("lrnTopActionClass");
		var ghost = $("<div>").addClass("lrnTopActionGhostClass").attr("aria-hidden", "true");
		var labels = [getRes("learnMeet_gotIt_label"), getRes("learnMiss_continue")];
		for (var i = 0; i < labels.length; i++) {
			ghost.append($("<span>").addClass("lrnCtaButtonClass lrnTopActionBtnClass").text(labels[i]));
		}
		slot.append(ghost);
		return slot;
	}

	// A right answer no longer auto-advances. On every rung, moving to the next word is an explicit
	// user action — the same top-right Continue button (or Enter) as a miss. This just parks the
	// forward action; the ladder pass + the advance run when the user chooses to go on.
	function offerAdvance(word) {
		setTopAction(getRes("learnMiss_continue"), function () { passWord(word); });
	}

	// A card that is a conjugated verb form AND that the server managed to place in a paradigm. Both
	// halves are needed: pos says it IS a form, verbLemma says there is a head verb to open and a
	// cell to mark. Without the placement there is neither, and the card is met and studied as an
	// ordinary word.
	function isVerbFormCard(w) {
		return Util.isVerbFormCard(w) && !!w.verbLemma;
	}

	// The English shape of the same thing: a principal-parts card ("bear, bore, borne"), whose head
	// verb the server reads straight off term.lemma. There is no paradigm CELL to mark — the card IS
	// the whole paradigm — so it opens the verb's entry with no highlight; the three forms are
	// printed there as the verb sense's conjugation chip, which is what the card asks for.
	function isVerbFormsCard(w) {
		return w.pos === Util.VERB_FORMS_POS && !!w.verbLemma;
	}

	// Is this card studied through its HEAD VERB instead of through its own entry? True of both verb
	// shapes, for one reason: neither card's own entry is worth opening. A conjugated combo's is a
	// single translation line; a principal-parts card's is that line plus the gloss the screen behind
	// it already shows — no IPA, no examples, no grammar, and in particular not the forms table. The
	// meet, its prefetch and the study link all ask THIS, so they can never disagree about which word
	// gets looked up.
	function studiesHeadVerb(w) {
		return isVerbFormCard(w) || isVerbFormsCard(w);
	}

	// The excursion itself: hide the session body, open `target`'s rich view over it, put everything
	// back on unwind. `seam` (verb-form cards only) names the form the excursion was launched from.
	// Shared by every study link on a Learn screen — a card's own headword, and any single word
	// clicked inside a phrase — so all of them come back the same way.
	function openStudyOverlay(target, seam, body) {
		if (!window.openLearnDrill) { return; }
		var covered = body.children();
		covered.hide();
		body.addClass("lrnBodyRichClass");   // dictionary-card layout: top-anchored, full width
		// Appended AFTER covered.hide(), so it is not one of the hidden children: the seam names
		// the form the excursion was launched from, which the card below (headed "abbrennen")
		// otherwise leaves no trace of.
		if (seam) { body.append(seam); }
		var overlay = $("<div>").addClass("lrnRevealRichClass");
		body.append(overlay);
		var prior = spaceAction;
		disarmSpace();   // don't let Enter advance while reading the rich view
		window.openLearnDrill(target, overlay, function () {
			if (seam) { seam.remove(); }
			overlay.remove();
			body.removeClass("lrnBodyRichClass");
			// NOT .show(): jQuery restores a div to display:block, which would flatten the
			// reveal strip's display:flex into a stacked column. Dropping the inline display
			// .hide() set hands each child back to its own CSS rule.
			covered.css("display", "");
			spaceAction = prior;
		});
	}

	// Turn a headword span into a "study this word" link: click opens its full rich view over the
	// current session body (Esc / Back returns, restoring whatever Enter was armed to). Same overlay
	// the miss reveal uses — shared so the R1 prompt term and the miss term behave identically.
	//
	// A verb form is studied through its HEAD VERB, for the reason meetVerbForm records: a combo's
	// own entry is one translation line and nothing else, so clicking "du branntest ab" used to buy
	// a card saying "du branntest ab — you burned down", which is what the screen behind it already
	// said. abbrennen's entry, with this form's paradigm cell marked, is the thing that explains the
	// miss — the distractors on a conjugated card come from the verb's own paradigm, so the
	// confusion is a sibling-cell confusion and the table is what resolves it.
	//
	// The same held for a principal-parts card ("bear, bore, borne") and was never applied to it
	// (owner report 2026-08-04): its own entry carries the head verb's translations and gloss and
	// stops there, so the study link bought a card repeating the question. "bear"'s entry is the one
	// that prints the forms — they are its verb sense's conjugation chip — plus the IPA, senses and
	// examples that say what the verb the forms belong to actually means.
	function bindTermStudy(span, w, body) {
		span.click(function () {
			var verbForm = studiesHeadVerb(w);
			// conjHighlight only for a conjugated combo — it names ONE cell of a paradigm grid, and a
			// principal-parts card has no cell to name (nor, on English entries, a grid to name it in).
			// What that card marks instead is the verb sense's principal parts, which ARE its paradigm
			// — and which are not always on the first sense: "bear"'s entry opens on the animal. The
			// two marks are alternatives; a combo's cell already says which sense it belongs to.
			var target = verbForm
				? { term: w.verbLemma,
				    conjHighlight: isVerbFormCard(w) ? w.term : null,
				    focusVerb: isVerbFormsCard(w) } : w;
			openStudyOverlay(target, verbForm ? verbFormSeam(w) : null, body);
		});
	}

	// ── one word inside a phrase is a study link ─────────────────────────────────
	//
	// A PHRASE has no entry worth opening. Looked up, "Welcher Anbieter ist bei euch am günstigsten?"
	// comes back as a single sense whose entire content is the translation the screen is already
	// showing beside it — a click that costs a screen and buys nothing (owner report 2026-08-06). The
	// words IN it are the lookup the learner actually wants, so every Learn surface that prints a
	// phrase links word by word instead of linking the phrase as a whole.
	//
	// Segmentation comes from main.js (window.termWordSegments) — the same one pass that decides what
	// a .term-word is inside a rich card, so "what counts as one word" is settled in one place. The
	// affordance is .lrnHwDrillClass, worn per word, which is also what makes the row read correctly:
	// each word underlined says "each of these is a link", where one underline under the whole
	// sentence said "this sentence is a link" — the thing that was never true.

	// This is for the screens that are FOR reading — the rung-0 meet and the recap. A QUESTION card
	// keeps its phrase inert on every rung (owner, 2026-08-06): the cue is there to be answered, and a
	// lookup hanging off every word of it invites the learner out of the question mid-answer.
	function studyWordLink(text, body) {
		var link = $("<span>").addClass("lrnHwDrillClass").text(text);
		link.assignTooltip("learnStudyTerm_tooltip");   // now true of the target: it IS one word
		link.click(function () { openStudyOverlay({ term: text }, null, body); });
		return link;
	}

	// `text` rendered word by word, each word its own study link, the punctuation between them inert.
	function phraseStudySpan(text, body) {
		var host = $("<span>");
		var segs = window.termWordSegments ? window.termWordSegments(text) : [];
		if (!segs.length) { return host.text(text); }
		for (var i = 0; i < segs.length; i++) {
			host.append(segs[i].word ? studyWordLink(segs[i].text, body)
				: document.createTextNode(segs[i].text));
		}
		return host;
	}

	// ── session chrome (top strip shared by every rung view) ─────────────────────

	function sessionTop() {
		var top = $("<div>").addClass("lrnSessionTopClass");
		var back = $("<button>").attr("type", "button").addClass("pl-btn-pill lrnBackLinkClass")
			.text(getRes("learnBackToTray_label"))
			.click(backToTray);
		// Two stacked zones, not columns: the ladder gets the whole band width and wraps freely, and
		// the two pills share a dedicated line under it — back at the left corner, forward action at
		// the right. Either pill sitting IN the ladder flow costs the dots a chunk of a line (and,
		// as a column beside them, a permanently empty lane).
		var strip = $("<div>").addClass("lrnStripClass");
		for (var i = 0; i < session.words.length; i++) {
			var word = session.words[i];
			if (word.done) {
				// A banned card leaves the window too, but as "known" (✓), not a graduate (the sprout).
				var done = $("<span>").addClass("lrnStripDoneClass");
				strip.append(word.known ? done.text("✓") : done.html(GARDEN.markIcon("sprout")));
			} else {
				strip.append(dotCluster(rungsPassed(word.rung), word === session.cur, ladder().length));
			}
		}
		// The ladder's own line. A 💬 sat in its top-right corner from 2026-08-25 until 2026-08-31,
		// when the owner moved the whole feedback channel to the right pane's "Send feedback" panel
		// (docs/project/user-feedback-feature.md) — this screen needed a door of its own then
		// because the tabs row hid for the whole of a Learn run (it stays since R15).
		// The row wrapper stays: it is what lets the ladder wrap freely inside a band whose height
		// is its own, and taking it away would put a `flex: 1 1 auto` strip straight into the
		// header's column flow.
		var stripRow = $("<div>").addClass("lrnStripRowClass");
		// The guided first session's dots say what they are (owner rulings R18 + R22): ONE fixed line
		// in front of them, in the owner's wording — "Üben · 5 Runden pro Wort" — and nothing else.
		// No rung names and no round counter ("I don't like adding words to rungs"). Its own flex
		// item, never part of the strip's wrap, so the label cannot end up between two clusters.
		if (session.guided) {
			stripRow.append($("<span>").addClass("lrnStripLabelClass").text(getRes("learnGuided_dots")));
		}
		stripRow.append(strip);
		top.append(stripRow);
		var actions = $("<div>").addClass("lrnStripActionsClass");
		actions.append(back);                  // left corner
		var slot = topActionSlot();            // right corner, pre-sized to its button
		actions.append(slot);
		top.append(actions);
		return top;
	}

	function sessionScreen(word) {
		hideCta();   // a session screen is not the tray — drop the "Start Learning!" band
		if (window.endLearnDrill) { window.endLearnDrill(); }   // a fresh card ends any lingering reveal drill
		disarmSpace();   // each screen re-arms Enter for its own primary button (if any)
		curOptions = null;   // no MC options until this screen builds a grid (buildOptions sets them)
		richRevealOpen = false;   // a fresh screen is never a flipped meet card (renderMeet re-sets it)
		meetUnflip = null;        // …so there is no flip to take back either
		stopTermSound();   // silence the word we're advancing FROM — it must not bleed into this new one
		var area = root().empty();
		area.append(sessionTop());
		var body = $("<div>").addClass("lrnBodyClass");
		area.append(body);
		return body;
	}

	// ── R0: the meet card ────────────────────────────────────────────────────────

	// The tense of a conjugated-form card, named in the learner's own textbook term («перфект») —
	// Util.conjTenseLine is the one definition, shared with Practice and with the dictionary's
	// conjugation table; the German original + descriptive name ride the chip's data-tense tooltip
	// (Util.conjTenseTip, delegated once in main.js). A tense the map doesn't know still renders,
	// under its raw key, so an added paradigm row can never blank the line.
	// The grammar label naming WHICH cell of a paradigm this card is: the tense, and — since
	// 2026-08-27 — the person, as two chips on one row.
	//
	// The person joined it because nothing else says who any more. A conjugated card used to carry a
	// per-cell translation whose pronoun said it («я говорил»), and that translation was invented per
	// cell and unsound by construction, so it is not shown anywhere (design §3,
	// docs/project/conj-card-and-answer-matching.md). The person is named in the LEARNER's language,
	// as a grammar label, because the German pronoun is the first word of the string the card is
	// about to ask for. Learn's comprehension rung is the single exception: it strips the pronoun off
	// the cue precisely so it can ask which one it was, and there the pronoun IS the option.
	//
	// Either half may be missing — a form stored with no recognizable subject pronoun has no person,
	// a form-SET card ("fahren, fährt, fuhr, ist gefahren") has neither — and the row is then dropped
	// rather than rendered empty.
	function slotRow(w) {
		var row = $("<div>").addClass("lrnTenseRowClass");
		var any = false;
		if (w.verbTense) {
			row.append($("<span>").addClass("pl-chip lrnTenseClass")
				.attr("data-tense", w.verbTense)
				.text(Util.conjTenseLine(w.verbTense)));
			any = true;
		}
		var person = Util.conjPersonLine(w.term);
		if (person) {
			row.append($("<span>").addClass("pl-chip lrnTenseClass").text(person));
			any = true;
		}
		return any ? row : null;
	}

	// The slot label for whatever cue is being built, or nothing when the card is not a paradigm
	// cell. Every rung calls this, not just the meet: a form-in-use card asks the same question at
	// every rung, so it has to describe itself the same way at every rung — and from rung 1 on the
	// wrong answers are other forms of the SAME verb, which makes the slot the thing being tested
	// rather than decoration.
	function cueSlotRow(w) {
		return isVerbFormCard(w) ? slotRow(w) : null;
	}

	// ── what a conjugated card MEANS ─────────────────────────────────────────────
	//
	// The head verb's senses, and never the cell's own stored translation. A paradigm cell is its own
	// term row with exactly ONE translation, picked per cell at mint time, and that pick cannot be
	// made honestly: sense 1 of `abbrennen` takes an inanimate subject, so eight of its twelve cells
	// have no literal reading and were filled with an idiom belonging to a different lemma — one
	// paradigm teaching two different verbs (design §2). The rule that replaced it: a cell's gloss is
	// a LABEL FOR A SLOT, never lexicography.
	//
	// A card whose head carries no taught verb sense arrives without them and keeps today's single
	// gloss line — a fallback, not a second design.

	/** How many senses a cue shows. The head verb's entry below the meet shows them all. */
	var HEAD_SENSES_SHOWN = 4;

	/** The head verb's senses for a conjugated card, or null when it has none to show. */
	function headSensesOf(w) {
		var senses = w && isVerbFormCard(w) && w.headSenses;
		return (senses && senses.length) ? senses : null;
	}

	/** The senses as the cue prints them: numbered, in entry order, one line each. */
	function promptSenses(senses) {
		var list = $("<ol>").addClass("lrnPromptSensesClass");
		for (var i = 0; i < Math.min(senses.length, HEAD_SENSES_SHOWN); i++) {
			list.append($("<li>")
				.append($("<span>").addClass("lrnSenseNumClass").text((i + 1) + "."))
				.append($("<span>").text((senses[i] || []).join(" · "))));
		}
		return list;
	}

	// What the cue SAYS, as plain text — the answer-event history's cue_shown, and the recap pills.
	// Follows the same branch the rendering does, so the history records what was on the screen.
	function cueMeaningText(w) {
		var senses = headSensesOf(w);
		if (!senses) { return w.gloss; }
		return senses.slice(0, HEAD_SENSES_SHOWN)
			.map(function (s) { return (s || []).join(" · "); }).join(" | ");
	}

	// The reveal for a verb form. It does NOT show the form's own dictionary entry: a combo's entry
	// is one translation line and nothing else — no IPA, no gloss, no examples, no grammar, true of
	// every one of the 21k pre-filled forms — so rendering it produced a reveal with essentially
	// nothing on it (owner report 2026-07-30). A principal-parts card's entry is barely more (the
	// head verb's translations and gloss, copied). What teaches the form is its meaning plus the
	// HEAD VERB's entry, so that is what lands here.
	//
	// The form's own head row stays visible above this (unlike the word meet, which hides it): the
	// card below is headed "abbrennen", not "ich brenne ab", so hiding the form would leave the
	// screen with no sign of the thing that was just asked.
	function meetVerbForm(w, host) {
		// A CONJUGATED cell prints no meaning of its own here: what it means is the head verb's
		// entry, which is rendered in full a few lines down, and the per-cell translation that used
		// to sit on this line was invented per cell (design §3). The head row above already names the
		// slot (tense + person). A principal-parts card keeps its line — that gloss IS the head
		// verb's, copied onto the card when it was minted, so it is not a per-cell invention.
		if (w.gloss && !isVerbFormCard(w)) {
			host.append($("<div>").addClass("lrnGlossClass lrnMeaningClass").text(w.gloss));
		}
		host.append($("<div>").addClass("lrnVerbFromClass")
			.append($("<span>").addClass("lrnVerbFromLabelClass").text(getRes("learnMeet_fromVerb_label")))
			.append($("<span>").addClass("lrnVerbFromTermClass").text(w.verbLemma)));
		// The lemma's entry is fetched by TEXT, the same trusted lookup every other reveal uses, so it
		// shares the rich-entry cache with the dictionary panel and costs nothing extra once warm.
		// Its own tokens stay drillable — clicking one opens that word over the session as usual.
		var entry = $("<div>").addClass("lrnVerbEntryClass");
		host.append(entry);
		// conjHighlight marks this form's own cell in the verb's paradigm table. Rung 0 opened that
		// table already; what it never did was say WHICH of the 36 cells it had just taught, which
		// left the learner to find "du branntest ab" by eye in a grid built out of near-misses.
		// A principal-parts card has no single cell — it is the whole paradigm — so it marks the verb
		// sense's principal parts instead, which is the same statement one level coarser and answers
		// an entry that opens on a noun ("bear" leads with the animal).
		if (window.renderLearnRichView) {
			window.renderLearnRichView({ term: w.verbLemma,
				conjHighlight: isVerbFormCard(w) ? w.term : null,
				focusVerb: isVerbFormsCard(w) }, entry);
		}
	}

	// The form + its meaning + "from the verb abbrennen", rendered ABOVE a head-verb card that was
	// opened from somewhere other than the meet (see bindTermStudy). Same two rows and the same
	// classes the revealed meet uses — .lrnVerbMeetClass is what left-aligns the form row so it lines
	// up with the dictionary card underneath instead of floating centred over it. Shared by both verb
	// shapes; the tense chip is what a principal-parts card simply has none of.
	function verbFormSeam(w) {
		var seam = $("<div>").addClass("lrnMeetClass lrnVerbMeetClass lrnVerbSeamClass");
		var head = $("<div>").addClass("lrnHwRowClass");
		head.append($("<span>").addClass("lrnHwClass").text(w.term));
		head.append(soundButton(w.termId, true, w.ipaVariantId));
		var seamSlot = slotRow(w);
		if (seamSlot) { head.append(seamSlot); }
		seam.append(head);
		// Same rule as the meet it mirrors: a conjugated cell's meaning is the head verb's entry
		// underneath, not a line of its own.
		if (w.gloss && !isVerbFormCard(w)) {
			seam.append($("<div>").addClass("lrnGlossClass lrnMeaningClass").text(w.gloss));
		}
		seam.append($("<div>").addClass("lrnVerbFromClass")
			.append($("<span>").addClass("lrnVerbFromLabelClass").text(getRes("learnMeet_fromVerb_label")))
			.append($("<span>").addClass("lrnVerbFromTermClass").text(w.verbLemma)));
		return seam;
	}

	function renderMeet(termCards) {
		var lead = termCards[0];   // representative card: carries session.cur + the meet history event
		if (window.VISIT_LOG) { VISIT_LOG.mark("rung", { i: session.words.indexOf(lead), rung: 0 }); }
		var w = lead.w;
		markAsked(lead, "TERM_TO_MEANING", displayTerm(w));   // the meet shows the word first
		var body = sessionScreen(lead);
		body.addClass("lrnBodyTopClass");   // starts where every other Learn screen does, 3rem down
		var card = $("<div>").addClass("lrnMeetClass");

		// A verb form — a conjugated combo ("ich brenne ab") or a principal-parts card ("bear, bore,
		// borne") — is met differently from a word: see meetVerbForm. Without a head verb there is
		// nothing to open and, for a combo, no tense to name, so such a card falls back to the
		// ordinary meet (studiesHeadVerb).
		var verbForm = studiesHeadVerb(w);

		// The reveal below always opens a rich view, so its lookup is deterministic the moment this
		// card renders — warm it now and the flip renders from cache. A no-op once the session-start
		// prefetch (or an earlier showing of the same word) has already warmed it; this call is what
		// covers a card that only reaches rung 0 later, after two consecutive misses.
		if (window.prefetchRichEntry) { window.prefetchRichEntry(meetLookupTerm(w)); }

		var head = $("<div>").addClass("lrnHwRowClass");
		if (w.article) {
			head.append($("<span>").addClass("pl-chip " + genderClass(w.article)).text(w.article));
		}
		head.append($("<span>").addClass("lrnHwClass").text(w.term));
		head.append(soundButton(w.termId, true, w.ipaVariantId));   // single word, room to breathe → big icon
		if (w.ipa) { head.append($("<span>").addClass("lrnIpaClass").text(ipaText(w.ipa))); }
		// The plural, labelled like the rich view ("pl. Bahnhöfe") so it reads as the plural form, not
		// a stray word; centred on its own line under the headword.
		if (w.plural) { head.append($("<span>").addClass("lrnPluralClass").text("pl. " + w.plural)); }
		// Which slot this form is — tense and person — on its own line under the headword. Shown
		// BEFORE the reveal on purpose: rung 0 tests nothing, it introduces, and "ich brenne ab"
		// without "Präsens · 1st pers. sg." makes the learner infer the one piece of context the card
		// exists to teach.
		var meetSlot = verbForm ? slotRow(w) : null;
		if (meetSlot) { head.append(meetSlot); }
		card.append(head);

		// Reveal shows the FULL rich view for the term (all senses), full width — same as any other
		// rich card. It carries its own headword/sound/IPA, so the guess head row hides on reveal.
		var rich = $("<div>").addClass("lrnRichClass").hide();
		card.append(rich);

		var reveal = $("<button>").attr("type", "button").addClass("pl-btn-pill lrnRevealClass")
			.text("🤔 " + getRes("learnMeet_reveal_label"));
		card.append(reveal);
		spaceTip(reveal);

		// "Got it →" graduates EVERY one of the term's rung-0 senses to R1 — the meet taught them all
		// at once. (Marking a word already-known is not here; it lives on the tray's ✓ toggle.)
		function gotIt() {
			for (var i = 0; i < termCards.length; i++) {
				termCards[i].rung = Math.max(termCards[i].rung, 1);
				postEvent(termCards[i], "meet");
			}
			advance();
		}

		var flipped = false;   // once ever per card: a flip undone by the walk's Back is not a second look
		reveal.click(function () {
			reveal.hide();
			body.addClass("lrnBodyRichClass");   // dictionary-card layout: top-anchored, full width
			rich.show();
			richRevealOpen = true;   // Escape may now leave the card the same way "Got it →" does
			if (verbForm) {
				card.addClass("lrnVerbMeetClass");   // the head row stays, so it left-aligns with the card below
				meetVerbForm(w, rich);
			} else {
				head.hide();   // the rich view carries its own headword (smaller since R5) — no double term
				if (window.renderLearnRichView) { window.renderLearnRichView(w, rich); }
			}
			if (!flipped) {
				lead.answeredAt = new Date().getTime();   // the flip is the meet's commit (0042 anchor)
				postEvent(lead, "reveal");   // history-only: they looked at the meaning
			}
			flipped = true;
			setTopAction(getRes("learnMeet_gotIt_label"), gotIt);   // forward action → top-right, Enter advances
			// …and the way back to the unflipped face, for the guided walk's Back on the flipped first
			// word (owner, 2026-10-08: Back there "should lead to the first word before the reveal").
			// Nothing the flip committed is undone — the reveal row stays written — only the screen.
			meetUnflip = function () {
				meetUnflip = null;
				if (window.endLearnDrill) { window.endLearnDrill(); }
				rich.hide().empty();
				body.removeClass("lrnBodyRichClass");
				card.removeClass("lrnVerbMeetClass");
				head.show();
				reveal.show();
				richRevealOpen = false;
				topActionHost().children("button").remove();
				armSpace(function () { reveal.click(); });
			};
		});

		body.append(card);
		lead.autoPlayed = true;   // the meet auto-pronounces on arrival (0042 auto_sound_played)
		playTermSound(w.termId, w.ipaVariantId);
		armSpace(function () { reveal.click(); });   // Enter flips the card
	}

	function appendPhrase(container, phrase) {
		if (!phrase || !phrase.term) { return; }
		var row = $("<div>").addClass("lrnPhraseRowClass");
		row.append($("<div>").addClass("lrnPhraseDeClass")
			.append($("<span>").addClass("lrnPhraseMarkClass").text("» "))
			.append($("<span>").text(phrase.term)));
		if (phrase.definition) {
			row.append($("<div>").addClass("lrnPhraseEnClass").text(phrase.definition));
		}
		container.append(row);
	}

	// ── phrase rungs (design §4) ─────────────────────────────────────────────────
	//
	// A collocation or an example sentence in the tray is a card like any other, and the plain rungs
	// answer themselves on it: against "The rumour turned out to be false." the generic options read
	// "table / to run / quickly" and only one of them is even a sentence. So the choice rungs take a
	// different shape for a phrase — a different one per KIND, because the two kinds teach different
	// things (§4.3). PhraseLearn decides the shape and supplies the material; everything below is
	// markup. Every branch returns false when it cannot make an honest question, and the plain rung
	// runs instead.

	function escapeHtml(s) {
		return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
	}

	// The phrase laid out around its focus word: `revealed` shows the word in place (the context line
	// of a meaning rung), otherwise the frame stands blank (the typing rungs' cue). Same spans
	// PhraseGap renders for the Practice cue — one owner for what a gapped frame looks like.
	// The frame's words are NOT study links, on any rung (owner, 2026-08-06). A question card is not a
	// place to read from: the cue exists to be answered, and hanging a lookup on every word of it
	// invites the learner out of the question they are in the middle of. Looking words up belongs on
	// the screens that are FOR reading — the rung-0 meet and the recap — where nothing is being asked.
	function frameLine(plan, revealed) {
		return $("<div>").addClass("lrnFrameClass").html(PhraseGap.frameHtml(plan, revealed));
	}

	// One gap-fill option: the whole sentence with the blank filled, the filler picked out so the eye
	// lands on the one thing that differs between the six.
	function filledHtml(plan, filler) {
		var html = "";
		for (var i = 0; i < plan.segments.length; i++) {
			var seg = plan.segments[i];
			html += seg.gap == null ? escapeHtml(seg.text)
				: "<b class='lrnFillClass'>" + escapeHtml(filler) + "</b>";
		}
		return html;
	}

	// R1 and R5 for a phrase: what does it mean?
	//   collocation — the unit itself is the lesson, so the options are other collocations' meanings.
	//   sentence    — the frame is context and the question is what the FOCUS WORD means in it;
	//                 four English sentences would mean inventing three plausible translations (§4.2).
	// `audio` is the sound rung: the phrase is heard, not shown, so the frame is left off — but the
	// question still names the headword, which is what keeps "what does it mean here" answerable.
	function renderPhraseMeaning(word, body, mode, audio) {
		var w = word.w;
		var question, cuePlan = null, options, correct;

		if (mode === "sentence") {
			var m = PhraseLearn.meaningPlan(w);
			if (!m) { return false; }
			question = strFormat(getRes("learnQ_phraseMeaning"), m.label);
			cuePlan = m.plan;
			options = deal(m.pool, 3).concat([m.correct]);
			correct = m.correct;
		} else {
			var pool = PhraseLearn.collocOptions(w);
			if (pool.length < 2) { return false; }
			question = getRes("learnQ_meaning");
			var defs = [];
			for (var i = 0; i < pool.length; i++) { defs.push(pool[i].definition); }
			options = deal(defs, 3).concat([w.gloss]);
			correct = w.gloss;
		}

		var prompt = $("<div>").append($("<div>").addClass("lrnQClass").text(question));
		// The sound rung's cue is the phrase SPOKEN, so no frame is drawn; otherwise the frame is
		// shown with its focus word marked in place — it is the word the question is about.
		prompt.append(audio
			? $("<div>").addClass("lrnSoundCueClass").append(soundButton(w.termId, true, w.ipaVariantId))
			: (cuePlan ? frameLine(cuePlan, true) : $("<div>").addClass("lrnFrameClass").text(w.term)));
		markAsked(word, "TERM_TO_MEANING", audio ? "🔊" : w.term);

		buildOptions(body, prompt, options, correct, word, "wide");
		word.autoPlayed = true;   // the phrase pronounces itself on arrival, spoken cue or not
		playTermSound(w.termId, w.ipaVariantId);
		return true;
	}

	// R2 for a phrase: produce it from the meaning.
	//   collocation — pick the phrase among other collocations (the unit is what is memorized).
	//   sentence    — one frame, the gap filled differently: pick the word that belongs in the blank.
	function renderPhraseProduce(word, body, mode) {
		var w = word.w;
		if (mode === "sentence") {
			var g = PhraseLearn.gapPlan(w);
			if (!g) { return false; }
			markAsked(word, "MEANING_TO_TERM", cueMeaningText(w));
			var fillers = deal(g.fillers, 5).concat([g.correct]);
			var opts = [];
			for (var i = 0; i < fillers.length; i++) {
				opts.push({ label: PhraseLearn.filled(g.plan, fillers[i]),
					html: filledHtml(g.plan, fillers[i]) });
			}
			buildOptions(body, promptGloss(getRes("learnQ_pickGap"), w.gloss), opts,
				PhraseLearn.filled(g.plan, g.correct), word, "wide");
			return true;
		}
		var pool = PhraseLearn.collocOptions(w);
		if (pool.length < 2) { return false; }
		markAsked(word, "MEANING_TO_TERM", cueMeaningText(w));
		var terms = [];
		for (var j = 0; j < pool.length; j++) { terms.push(pool[j].term); }
		buildOptions(body, promptGloss(getRes("learnQ_pickPhrase"), w.gloss),
			deal(terms, 3).concat([w.term]), w.term, word, "wide");
		return true;
	}

	// ── choice rungs (R1 word→gloss, R2 gloss→word, R3 look-alikes, R5 sound) ────

	function renderChoice(word) {
		var w = word.w;
		var body = sessionScreen(word);
		var options, prompt, six = false;
		var phraseMode = window.PhraseLearn ? PhraseLearn.mode(w) : null;

		if (phraseMode && word.rung === 1 && renderPhraseMeaning(word, body, phraseMode, false)) { return; }
		if (phraseMode && word.rung === 2 && renderPhraseProduce(word, body, phraseMode)) { return; }

		if (word.rung === 1) {
			markAsked(word, "TERM_TO_MEANING", displayTerm(w));
			prompt = $("<div>");
			prompt.append($("<div>").addClass("lrnQClass").text(getRes("learnQ_meaning")));
			var pw = $("<div>").addClass("lrnPromptWordClass");
			// The headword is a study link — click opens its full rich view (underlined like every
			// drill term). The reveal below no longer repeats it (it's shown, and clickable, here).
			var pwLink = $("<span>").addClass("lrnPromptTermClass lrnHwDrillClass").text(displayTerm(w));
			bindTermStudy(pwLink, w, body);
			pwLink.assignTooltip("learnStudyTerm_tooltip");
			pw.append(pwLink).append($("<span>").text(" "));
			pw.append(soundButton(w.termId, true, w.ipaVariantId));   // single word, room to breathe → big icon
			if (w.ipa) { pw.append($("<span>").addClass("lrnIpaClass").text(" " + ipaText(w.ipa))); }
			prompt.append(pw);
			// The form is on screen here, but its NAME is not: "ich sprach" only says Präteritum to
			// someone who already knows the paradigm, which is the thing this card is teaching.
			// (Unreachable for a conjugated card since 2026-08-27 — renderRung routes those to the
			// comprehension rung or past R1 entirely; the row stays because the guard inside it is
			// what keeps every OTHER card's cue unchanged.)
			var r1Slot = cueSlotRow(w);
			if (r1Slot) { prompt.append(r1Slot); }
			options = deal(w.r1, 3).concat([w.gloss]);
			buildOptions(body, prompt, options, w.gloss, word, false);
			word.autoPlayed = true;   // R1 auto-pronounces its prompt word (0042 auto_sound_played)
			playTermSound(w.termId, w.ipaVariantId);
			return;
		}
		if (word.rung === 2) {
			markAsked(word, "MEANING_TO_TERM", cueMeaningText(w));
			prompt = promptGloss(getRes("learnQ_pickWord"), w.gloss, w);
			var opts = deal(w.r2, 3).concat([{ term: w.term, article: w.article }]);
			var labels = [];
			for (var i = 0; i < opts.length; i++) {
				labels.push((opts[i].article ? opts[i].article + " " : "") + opts[i].term);
			}
			buildOptions(body, prompt, labels, displayTerm(w), word, formSetLayout(w, false));
			return;
		}
		// rung 3 — six bare look-alike forms, plus the seventh card from the session's 2nd R3 on
		markAsked(word, "MEANING_TO_TERM", cueMeaningText(w));
		prompt = promptGloss(getRes("learnQ_lookalike"), w.gloss, w);
		session.r3Cards++;
		// Not on the session's first R3 card — that one is a straight discrimination, so the format
		// is met before the twist. "None is right" also needs six real wrong forms, so a thin
		// look-alike pool never draws it.
		var noneShown = session.r3Cards > 1;
		var noneRight = noneShown && (w.r3 || []).length >= 6
			&& Math.floor(Math.random() * NONE_RATE) === 0;
		word.noneTaught = noneRight;
		options = noneRight ? deal(w.r3, 6) : deal(w.r3, 5).concat([w.term]);
		buildOptions(body, prompt, options, noneRight ? getRes("learnOpt_none") : w.term, word,
			formSetLayout(w, true), noneShown ? getRes("learnOpt_none") : null);
	}

	// A form-set card's options are whole recited paradigms — "fahren, fährt, fuhr, ist gefahren"
	// beside five near-identical siblings. That is the same reading problem a collocation has and
	// wants the same answer: one full-width column, so the six are scanned DOWN and the eye lands on
	// the slot that differs. In the two- and three-across grids they wrap to three lines each at
	// 1.85rem and the discrimination the rung exists for becomes a puzzle about line breaks.
	//
	// It matters more since the options became wrong forms of the same verb: they used to be short
	// unrelated words standing beside one long face, which read as lopsided but at least fit.
	function formSetLayout(w, otherwise) {
		return Util.isVerbFormsCard(w) ? "wide" : otherwise;
	}

	// ── R1 on a form-in-use card: the comprehension rung ─────────────────────────
	//
	// The one change on the form-in-use ladder with direct evidence behind it
	// (docs/project/verb-card-types.md, "Form-in-use — nearly right; one addition"): VanPatten's
	// processing-instruction studies compared mechanical output drills against comprehension-based
	// INPUT practice on exactly this morphology, and the input group improved on interpretation AND
	// production while the output group improved on production only. Drilling "du fuhrst" to death
	// makes you fast at typing "du fuhrst"; it does not make you understand it when you hear it.
	//
	// So the card is shown its own form and asked what it SAYS — who, and when:
	//
	//                            fährst
	//                        Who, and when?
	//        the pronoun is left out on purpose — the ending says who
	//
	//     [ ты едешь   Präsens · настоящее    ]  ← ✓
	//     [ ты ехал    Präteritum · простое прошедшее ]
	//     [ я еду      Präsens · настоящее    ]
	//     [ он едет    Präsens · настоящее    ]
	//
	// Three decisions the design left open, and the reasons:
	//
	// 1. IT REPLACES R1, it is not an eighth rung. The design's table says "add", and adding is what
	//    RUNG_COUNT forbids: the number is the SRS ladder, the finish screen, the resume path and —
	//    unlike step 5's client-only RUNG_COUNT — UserSenseLearning.GRADUATED_RUNG, which is written
	//    into every user_sense_learning row already on disk. Changing it re-reads existing rows'
	//    meaning. R1 is where it belongs anyway: R1 already shows the form and asks what it means,
	//    which is this question with the two hard parts taken out.
	//
	// 2. THE PRONOUN COMES OFF, and that is the difference between this rung and the R1 it replaces.
	//    "du fuhrst" prints the person in the question — the same free pass step 5 just removed from
	//    R4's "fa" — and the evidence cited above is precisely about learners reading the lexical
	//    cue (the pronoun) and never processing the ending. The auxiliary STAYS: "hast gesprochen"
	//    keeps its person marking, which is morphology, not a lexical crutch.
	//
	// 3. ITS OPTIONS ARE (PRONOUN, TENSE) PAIRS, not grammar labels to be typed. Asking the learner
	//    to name "2. Person Singular Präteritum" tests metalanguage, which is the mechanical drill
	//    processing instruction was contrasted WITH. Both halves are things the learner has been
	//    shown all along: the pronoun is what the cue had lifted off, and the tense is the chip every
	//    cue on this card wears.
	//
	//    They were (MEANING, TENSE) pairs until 2026-08-27, and that was two problems in one. The
	//    meanings were the invented per-cell glosses (design §2), and because no two cells of a verb
	//    ever shared one, four cells of one verb read as four different verbs — so the rung was
	//    answerable by matching phrases without ever parsing "bin + abgebrannt", which is the exact
	//    lexical shortcut point 2 exists to close.
	//
	// The rung stays silent: the clip recites the pronoun, so playing it would BE the answer. The
	// pick's correct branch plays it (optionButton), which is the right moment for it.

	// Wrong slots dealt beside the right one, and the smallest pool worth dealing from. Twinned with
	// VerbSlotOptions.MIN_SLOTS (Java) — the server ships nothing below it, and this is the guard
	// that keeps an old cached payload from producing a two-option card.
	var COMP_DISTRACTORS = 3;

	// The form with its subject pronoun taken off — the cue. Util.conjPronounPrefix matches the whole
	// pronoun LABEL ("er, sie, es"), never a split at the first space.
	function bareForm(w) {
		var pronoun = Util.conjPronounPrefix(w.term);
		return pronoun ? w.term.substring(pronoun.length).trim() : w.term;
	}

	// Does this card carry the comprehension rung?
	// A card carries the rung when everything here holds; any of them failing routes it past R1
	// (renderRung) rather than onto a broken question:
	//  - it is a paradigm cell with a tense to name;
	//  - the server found it enough sibling cells (VerbSlotOptions decides what "enough" means);
	//  - the pronoun actually came off. A cell stored with an unrecognized subject — a parenthetical,
	//    a `sie`-initial 3sg — would otherwise print the person in the question, and languages with
	//    no subject pronoun at all (Spanish, when it arrives) will need their own answer here rather
	//    than inheriting German's by accident.
	//
	// A meaning is no longer among the conditions: the options are (person, tense) pairs, both facts
	// about the paradigm, so a card whose siblings nobody has translated carries the rung fine.
	function hasComprehension(w) {
		return isVerbFormCard(w) && !!w.verbTense
			&& !!w.slots && w.slots.length >= COMP_DISTRACTORS
			&& bareForm(w) !== w.term;
	}

	// One option: the subject pronoun, and under it the tense chip every other cue on this card
	// wears. Both halves are in the judged label, because the pair IS the answer — the two cells this
	// rung exists to separate ("ich sprach" / "ich habe gesprochen") are the same person in two
	// tenses and differ only there.
	//
	// The PRONOUN names the person here, not the grammar label every other screen uses. This is the
	// one cue with the pronoun taken off, so the pronoun is literally the thing being asked for, and
	// it gives nothing away; naming it "1st pers. sg." instead would test metalanguage, which this
	// rung's own design (VanPatten's processing instruction, below) exists to avoid.
	function slotOption(person, tense) {
		var line = Util.conjTenseLine(tense);
		return {
			label: person + " · " + line,
			html: "<span class='lrnSlotPersonClass'>" + escapeHtml(person) + "</span>"
				+ "<span class='pl-chip lrnTenseClass lrnSlotTenseClass'>" + escapeHtml(line) + "</span>"
		};
	}

	function renderComprehension(word) {
		var w = word.w;
		var cue = bareForm(w);
		markAsked(word, "TERM_TO_MEANING", cue);
		var body = sessionScreen(word);
		var prompt = $("<div>");
		prompt.append($("<div>").addClass("lrnQClass").text(getRes("learnQ_whoWhen")));
		prompt.append($("<div>").addClass("lrnPromptWordClass lrnBareFormClass").text(cue));
		// Said out loud, because a missing pronoun on a screen that has shown one at every other rung
		// reads as a bug rather than as the question. It also names what to read instead.
		prompt.append($("<div>").addClass("lrnBareNoteClass").text(getRes("learnQ_whoWhenHint")));

		// The FIRST slot is always dealt, which is why the server bothers to order the pool at all
		// (VerbSlotOptions): a sibling that shares this card's gloss makes the morphology the only
		// way to answer, and that is the question worth asking every single time. The other two are
		// dealt for variety, and buildOptions shuffles all four for display.
		var picked = [w.slots[0]].concat(deal(w.slots.slice(1), COMP_DISTRACTORS - 1));
		var labels = [];
		for (var i = 0; i < picked.length; i++) {
			labels.push(slotOption(picked[i].person, picked[i].tense));
		}
		// The right answer is the card's OWN coordinates, and both halves are already on it: the
		// pronoun is the one this cue was built by stripping (bareForm above), the tense is the
		// server's placement of the cell.
		var correct = slotOption(Util.conjPronounPrefix(w.term), w.verbTense);
		labels.push(correct);
		buildOptions(body, prompt, labels, correct.label, word, "wide");
	}

	function renderSound(word) {
		var w = word.w;
		var body = sessionScreen(word);
		// A phrase's sound rung asks the same question its R1 does, minus the frame on screen: what
		// was heard IS the frame. (renderPhraseMeaning marks the asking itself.)
		var phraseMode = window.PhraseLearn ? PhraseLearn.mode(w) : null;
		if (phraseMode && renderPhraseMeaning(word, body, phraseMode, true)) { return; }
		markAsked(word, "TERM_TO_MEANING", "🔊");   // sound-only cue: the spoken word is the cue
		var prompt = $("<div>");
		prompt.append($("<div>").addClass("lrnQClass").text(getRes("learnQ_hear")));
		prompt.append($("<div>").addClass("lrnSoundCueClass")
			.append(soundButton(w.termId, true, w.ipaVariantId)));   // big icon, centred filled cue
		// Naming the slot gives nothing away here — the answer is the MEANING, not the slot — and
		// without it the heard form is the one cue in the ladder with no context at all.
		// (Unreachable for a conjugated card since 2026-08-27: renderRung skips R5 on those, because
		// every option would be the same meaning. See the comment there.)
		var sndSlot = cueSlotRow(w);
		if (sndSlot) { prompt.append(sndSlot); }
		var options = deal(w.r1, 3).concat([w.gloss]);
		buildOptions(body, prompt, options, w.gloss, word, false);
		word.autoPlayed = true;   // the sound rung's cue IS the auto-played word (0042)
		playTermSound(w.termId, w.ipaVariantId);
	}

	// The learn feedback line as one HTML string (span + face SVG), matching how Practice builds
	// its own result text — a single string handed to .html() rather than jQuery-appended pieces,
	// since jQuery's older fragment builder doesn't reliably parse a bare <svg> tag.
	function feedbackMarkup(good) {
		return "<span>" + getRes(good ? "learnFeedback_correct" : "learnFeedback_wrong") + "</span>"
			+ WrongFeedback.moodSvg(good ? "good" : "bad", "lrnFeedbackFaceClass");
	}

	// Swap the hint band from its instruction to its verdict. The hint is hidden, not removed, so it
	// keeps holding the band open at its own height.
	function showVerdict(slot, good) {
		slot.children(".lrnHintTextClass").css("visibility", "hidden");
		slot.children(".lrnVerdictClass").html(feedbackMarkup(good));
	}

	// The ONE cue builder every meaning→term rung uses — R2 (pick the word), R3 (look-alikes), R4
	// (type how it starts), R6 (type it in full). `w` is optional; passing a phrase or a plain word
	// leaves it exactly as it was, one gloss line and nothing else.
	//
	// On a conjugated card it is what makes all four rungs obey the rule at once: the meaning shown
	// is the HEAD VERB's senses, numbered, and the slot is named by the grammar label under them
	// (design §3). That is why the word is threaded through — the alternative was four call sites
	// each deciding for itself what a paradigm cell means.
	function promptGloss(question, gloss, w) {
		var p = $("<div>");
		p.append($("<div>").addClass("lrnQClass").text(question));
		var senses = headSensesOf(w);
		p.append(senses ? promptSenses(senses)
			: $("<div>").addClass("lrnPromptGlossClass").text(gloss));
		// Under the meaning, above the answer: the meaning is the question and the slot narrows it,
		// so it reads as part of the ask rather than as a label on the answer.
		var slot = w ? cueSlotRow(w) : null;
		if (slot) { p.append(slot); }
		return p;
	}

	// `labels` items are plain strings, or {label, html} where the option needs markup — a gap-fill
	// option is a whole sentence with the filler picked out, and only the filler differs between six
	// otherwise identical labels. `label` stays the key the pick is judged on either way.
	//
	// `layout`: falsy = the default two-column grid, true = the six-way look-alike grid, "wide" = one
	// full-width column, which is what phrase options need — a collocation or a sentence does not fit
	// half a row, and side-by-side sentences are unreadable.
	// `tail`: an option appended AFTER the grid's own options and never shuffled into them — R3's
	// "none of these" card. It is a full member of the option set (same resolve, same keyboard digit,
	// same correct/wrong treatment); it just always sits in the same place.
	function buildOptions(body, prompt, labels, correctLabel, word, layout, tail) {
		var six = layout === true;
		body.addClass("lrnBodyTopClass");   // a question screen: anchored, so option wrapping can't move it
		body.append(prompt);
		// 0042: identifies the real MC task (thin pools shrink it). The tail card counts — it is an
		// option the learner has to weigh, not chrome.
		word.optionsCount = labels.length + (tail ? 1 : 0);
		// The how-to-answer hint and the verdict that takes its place share ONE band right above the
		// grid: both are appended now, stacked in the same CSS grid cell, and a pick only flips which
		// one is visible (showVerdict). Because both are always laid out, the band's height never
		// changes and the grid below it never jumps. A wrong pick still gets its retry line + the
		// answer strip below the grid (showRevealRetry); only the "Correct!" / "Not quite" phrase
		// itself lives up here.
		var hint = $("<div>").addClass("lrnHintSlotClass");
		hint.append($("<div>").addClass("lrnHintTextClass").text(getRes("learnQ_pickHint")));
		hint.append($("<div>").addClass("lrnVerdictClass"));
		body.append(hint);
		var grid = $("<div>").addClass("lrnOptGridClass"
			+ (six ? " lrnOptSixClass" : "") + (layout === "wide" ? " lrnOptWideClass" : ""));
		var shuffled = shuffle(labels);
		curOptions = [];   // exposed to the keydown handler so 1-9 can pick an option
		for (var i = 0; i < shuffled.length; i++) {
			var btn = optionButton(shuffled[i], correctLabel, word, grid, body, i + 1, hint);
			curOptions.push(btn);
			grid.append(btn);
		}
		if (tail) {
			// Its own full-width row under the six, on a face of its own — a seventh CARD, not a
			// control. A button below the grid would read as chrome ("skip", "I don't know") and be
			// clicked for the wrong reason; this has to read as a real answer, because it is one.
			var tailBtn = optionButton(tail, correctLabel, word, grid, body, shuffled.length + 1, hint);
			tailBtn.addClass("lrnOptNoneClass");
			curOptions.push(tailBtn);
			grid.append(tailBtn);
		}
		body.append(grid);
		reserveReveal(body, word);
	}

	// num (1-based) draws a corner badge and is the key that picks this option. The label lives in a
	// data attr (not just as text) so the badge digit can't leak into the correct-answer text compare.
	function optionButton(option, correctLabel, word, grid, body, num, hint) {
		var label = option && option.label != null ? option.label : option;
		var btn = $("<button>").attr("type", "button").addClass("lrnOptClass").attr("data-opt-label", label);
		if (num) { btn.append($("<span>").addClass("lrnOptNumClass").text(num)); }
		// data-label feeds the CSS bold ghost that pre-sizes the option for its picked state.
		var text = $("<span>").addClass("lrnOptTextClass").attr("data-label", label);
		btn.append(option && option.html ? text.html(option.html) : text.text(label));
		btn.click(function () {
			if (grid.data("lrnResolved")) { return; }
			grid.data("lrnResolved", true);
			grid.find(".lrnOptClass").prop("disabled", true).addClass("lrnOptFadedClass");
			word.answeredAt = new Date().getTime();   // the commit — the 0042 latency anchor
			word.attempts = 1;                        // an MC pick is always one attempt
			word.lastAnswer = label;   // carried into the pass/fail event's history row
			var good = label === correctLabel;
			if (good) {
				btn.removeClass("lrnOptFadedClass").addClass("lrnOptCorrectClass");
				showVerdict(hint, true);
				if (word.rung !== 5) { playTermSound(word.w.termId, word.w.ipaVariantId); }
				// A card whose right answer is an ABSENCE teaches nothing unless the real form is
				// then shown and named (design §7), so a correct "none of these" ends on the same
				// strip a miss does. Before offerAdvance, which owns the top action.
				if (word.noneTaught) { showRevealTaught(body, word); }
				offerAdvance(word);   // no auto-advance — the user moves on with Continue / Enter
			} else {
				btn.removeClass("lrnOptFadedClass").addClass("lrnOptWrongClass");
				grid.find(".lrnOptClass").each(function () {
					if ($(this).attr("data-opt-label") === correctLabel) {
						$(this).removeClass("lrnOptFadedClass").addClass("lrnOptCorrectClass");
					}
				});
				// The verdict reads in the pick-hint's own slot, above the grid — the retry line and
				// the answer strip (word + sound) still land below it (showRevealRetry).
				showVerdict(hint, false);
				showRevealRetry(body, word);
			}
		});
		return btn;
	}

	// The miss reveal: re-teach the word + sound, then Continue. Continue lands in the uniform
	// top-right slot (setTopAction). The word itself is shown here — clickable → its full rich view,
	// underlined like every drill link — EXCEPT on R1 (word→meaning), where the headword is already
	// the prompt above (shown + clickable): repeating it there would just be the "we already have it
	// above" duplicate, so R1 skips the strip and shows only the note below.
	//
	// Both pieces are BUILT WITH THE SCREEN by reserveReveal below, but NOT appended until actually
	// needed (ensureRevealAppended, called from showRevealStrip): these are all top-anchored screens
	// (.lrnBodyTopClass), so appending them later only grows the bottom on a real miss — nothing
	// above them moves. Appending up front on every screen, hidden or not, still reserves their full
	// height in the layout (visibility:hidden keeps the box), which on a tall six-option grid was
	// exactly the blank space below the last card that a scroll landed on for no reason (owner
	// report 2026-07-31). The note's placeholder text ("still settling") never actually reaches the
	// user — showRevealVerdict / showRevealRetry always overwrite it before it's appended.
	function reserveReveal(body, word) {
		var w = word.w;
		var strip = null;
		// R1's prompt IS the headword, so repeating it below would be the "we already have it above"
		// duplicate — except on the comprehension rung, whose prompt is the form with the pronoun
		// taken OFF. There the strip is the thing a miss most needs: the whole form, restored.
		if (word.rung !== 1 || word.compRung) {
			strip = $("<div>").addClass("lrnRevealStripClass lrnRevealPendingClass");
			var wordSpan = $("<span>").addClass("lrnRevealWordClass lrnHwDrillClass").text(displayTerm(w));
			wordSpan.assignTooltip("learnStudyTerm_tooltip");
			bindTermStudy(wordSpan, w, body);   // click → full rich view over the rung; Esc / Back returns
			strip.append(wordSpan);
			// Mic · Sound, the app-wide order minus the Add control Learn does not have.
			var mic = micButton(w, true);
			if (mic) { strip.append(mic); }
			strip.append(soundButton(w.termId, true, w.ipaVariantId));   // single word, room to breathe → big icon
			if (w.ipa) { strip.append($("<span>").addClass("lrnIpaClass").text(ipaText(w.ipa))); }
		}
		var note = $("<div>").addClass("lrnRevealNoteClass lrnRevealPendingClass")
			.text(getRes("learnMiss_settling"));
		body.data("lrnReveal", { strip: strip, note: note });
	}

	// First call after reserveReveal actually appends the strip/note; later calls (showRevealVerdict
	// then showRevealStrip, same miss) are no-ops.
	function ensureRevealAppended(body) {
		var r = body.data("lrnReveal");
		if (r && !r.appended) {
			if (r.strip) { body.append(r.strip); }
			body.append(r.note);
			r.appended = true;
		}
	}

	function showRevealStrip(body, word) {
		var w = word.w;
		ensureRevealAppended(body);
		setTopAction(getRes("learnMiss_continue"), function () { failWord(word); });   // Enter = Continue
		body.find(".lrnRevealPendingClass").removeClass("lrnRevealPendingClass");
		playTermSound(w.termId, w.ipaVariantId);
	}

	// Every rung's miss ends the same way: the word once (skipped on R1, already shown as the
	// prompt), then the verdict under it — "Not quite" + face, "you will have another try" — in
	// the reserved note slot, replacing the settling line. Shared by optionButton's wrong branch
	// (card rungs) and renderType's miss path (typing rungs) so a missed card and a missed typed
	// answer read identically.
	function showRevealVerdict(body, word) {
		ensureRevealAppended(body);
		body.find(".lrnRevealNoteClass").addClass("lrnRevealVerdictClass")
			.html(feedbackMarkup(false))
			.append($("<div>").addClass("lrnAnswerRetryClass").text(getRes("learnFeedback_retry")));
		showRevealStrip(body, word);
	}

	// The "none of these" WAS right: the same strip a miss unhides — the word, its sound, its IPA —
	// but with no retry line, because nothing went wrong. The sound is already played by the
	// correct-pick branch that calls this, so it is not played again here.
	function showRevealTaught(body, word) {
		ensureRevealAppended(body);
		body.find(".lrnRevealNoteClass").addClass("lrnRevealVerdictClass")
			.empty()
			.append($("<div>").addClass("lrnAnswerRetryClass").text(getRes("learnNone_taught")));
		body.find(".lrnRevealPendingClass").removeClass("lrnRevealPendingClass");
	}

	// The card-pick wrong path (optionButton): the verdict phrase itself now reads in the pick-hint
	// above the grid, so this note only carries the retry line — the strip (word + sound) still
	// unhides exactly the way showRevealVerdict's does.
	function showRevealRetry(body, word) {
		ensureRevealAppended(body);
		body.find(".lrnRevealNoteClass").addClass("lrnRevealVerdictClass")
			.empty()
			.append($("<div>").addClass("lrnAnswerRetryClass").text(getRes("learnFeedback_retry")));
		showRevealStrip(body, word);
	}

	// ── R4: cued production — type how it starts ─────────────────────────────────

	// The typing cue targets the first content word: a leading "sich" (reflexives) is shown as
	// given text, like the article — producing the pronoun is not the point of the rung.
	// The subject pronoun a conjugated-form card opens with ("ich", "er, sie, es"), or "" for any
	// other card. Matched against the known pronoun LABELS, never split at the first space: a
	// third-person form is drilled as the whole enumeration, and a naive split would hand back "er,".
	// Same helper Practice's answer prefix uses, so both tabs agree on where the pronoun ends.
	function conjPronoun(w) {
		return Util.isVerbFormCard(w) ? Util.conjPronounPrefix(w.term) : "";
	}

	// A conjugated form's pronoun is given here too, and at this rung that is arithmetic rather than
	// preference: the rung asks for the first two letters, so a typed pronoun would make the answer
	// to "ich beschliesst" be "ic". The Practice pronoun-prefill setting therefore does NOT reach
	// this rung; it governs R6, the full-typing rung that mirrors the drill.
	function typingCue(term, w) {
		var parts = term.split(" ");
		var idx = 0;
		if (parts.length > 1 && fold(parts[0]) === "sich") { idx = 1; }
		var pronoun = conjPronoun(w);
		if (pronoun) { idx = pronoun.split(" ").length; }
		var core = parts[idx];
		var n = Math.min(2, core.length);
		return {
			lead: parts.slice(0, idx).join(" "),
			core: core,
			n: n,
			tail: parts.length > idx + 1
		};
	}

	// R4's aid ladder, counted in misses charged to a SINGLE cue letter: the 3rd miss on a letter
	// earns that letter's aid, the 5th reveals the word and fails the rung.
	var R4_AID_AT = 3, R4_REVEAL_AT = 5;

	function renderType(word) {
		var w = word.w;
		markAsked(word, "MEANING_TO_TERM", cueMeaningText(w));
		var body = sessionScreen(word);
		body.addClass("lrnBodyTopClass");   // question screen — anchored like the choice rungs
		body.append(promptGloss(getRes("learnQ_start"), w.gloss, w));

		// A phrase asks for its focus word and nothing else (§4): the frame stands above with the
		// word blanked, so what the two letters belong to is visible without being given away.
		var typed = window.PhraseLearn ? PhraseLearn.typingPlan(w) : null;
		if (typed) { body.append(frameLine(typed.plan, false)); }

		var cue = typingCue(typed ? typed.first : w.term, w);
		// The letters this rung asks for, and every spelling accepted for them — the SAME generator
		// R6 and Practice's rule agree on. It matters here because a digraph spelling is longer than
		// the letters it stands for: the first two letters of "kümmern" are "kü", but "kue" is three
		// characters, so the field has to admit the longest accepted variant rather than cue.n.
		var cueTarget = cue.core.substring(0, cue.n);
		var cueVariants = typingVariants(cueTarget);
		var cueMaxLen = cueTarget.length;
		for (var vi = 0; vi < cueVariants.length; vi++) {
			cueMaxLen = Math.max(cueMaxLen, cueVariants[vi].text.length);
		}
		var rowHost = $("<div>");
		var row = $("<div>").addClass("lrnTypeRowClass");
		if (w.article) { row.append($("<span>").addClass("lrnTypeLeadClass").text(w.article)); }
		if (cue.lead) { row.append($("<span>").addClass("lrnTypeLeadClass").text(cue.lead)); }
		// One character of slack over the longest accepted spelling: a rejected keystroke STAYS in the
		// field (see the input handler), so the field has to hold one more character than the answer
		// for "type straight past the bad one" to be physically possible. Without the slack maxlength
		// swallows the correcting keystroke and backspace becomes the only way out.
		var input = $("<input>").attr({ type: "text", maxlength: cueMaxLen + 1, autocomplete: "off", spellcheck: "false" })
			.addClass("lrnLetterInputClass");
		row.append(input);
		var blanks = "";
		for (var i = cue.n; i < cue.core.length; i++) { blanks += "_ "; }
		if (cue.tail) { blanks += "…"; }
		row.append($("<span>").addClass("lrnGhostClass").text(blanks.replace(/\s+$/, "")));
		rowHost.append(row);
		body.append(rowHost);

		var hint = $("<div>").addClass("lrnTypeHintClass")
			.text(typed ? strFormat(getRes("learnQ_typeGapHint"), cue.n)
				: strFormat(getRes("learnQ_typeHint"), cue.n));
		body.append(hint);
		var subst = typeSubstLine();   // "ä→ae  ö→oe …" — under the field, since the field is 2ch wide
		if (subst) { body.append(subst); }
		reserveReveal(body, word);

		// The ladder is counted per LETTER of the cue, not in total. Per-letter is what keeps an aid
		// honest: a user who nails "A" and then flounders on "u" has not earned "it starts with A" —
		// they just produced it — they have earned the sound. The same budget also caps a user still
		// missing letter 1 after it has been named: the hint has told them, so typing it and moving on
		// to letter 2 is the whole remaining ask, and looping there forever isn't a state worth
		// holding open.
		var misses = [];      // wrong keystrokes charged to each cue letter, index 0 / 1
		var slips = 0;        // every wrong keystroke — the 0042 effort count
		var badAt = -1;       // val position of the currently-shown wrong keystroke (-1 = none)
		trackFirstKey(input, word);   // 0042: first_key_ms measured in the rung's own input

		function land(val) {
			if (!word.answeredAt) { word.answeredAt = new Date().getTime(); }   // the commit — 0042 latency anchor
			word.lastAnswer = val;       // the typed letters, for the history row
			word.attempts = slips + 1;   // R4 effort counter: wrong tries + the landing one
			var bloom = $("<div>").addClass("lrnBloomClass");
			if (w.article) { bloom.append($("<span>").addClass("lrnTypeLeadClass").text(w.article)); }
			bloom.append($("<span>").text(w.term));
			bloom.append(soundButton(w.termId, true, w.ipaVariantId));   // app-wide big volume icon, not a 🔊 emoji
			rowHost.empty().append(bloom);
			hint.text("");
			if (subst) { subst.remove(); }   // the letters are in — nothing left to spell
			playTermSound(w.termId, w.ipaVariantId);
			offerAdvance(word);   // no auto-advance after the letters land — Continue / Enter moves on
		}

		input.on("input", function () {
			var val = input.val();
			// A wrong keystroke STAYS in the field, in place, until the user overrides it — by deleting
			// it or by typing straight past it. The latter arrives as an append, so splice the stale bad
			// character out before judging the new one. (Same rule as R6. The old behaviour here emptied
			// the WHOLE field on every miss, which threw away letters already got right: after a correct
			// "A", typing "D" cost the user the "A" as well and sent them back to nothing.)
			if (badAt !== -1 && val.length > badAt + 1) {
				val = val.slice(0, badAt) + val.slice(badAt + 1);
				input.val(val);
			}
			var norm = typedNorm(val);   // typedNorm is 1:1, so every index below is valid in `val` too

			// Judge only once the typing can no longer become one of the accepted spellings: while
			// `norm` is still a prefix of some variant the user may be mid-digraph ("ku" on its way to
			// "kue"), so stay quiet. Two passes so neither result depends on variant order, exactly as
			// R6 does it: an exact match on ANY variant lands; a valid prefix under any variant is not
			// yet an error; only when no variant accepts it is it a genuine wrong keystroke.
			var ci;
			for (ci = 0; ci < cueVariants.length; ci++) {
				if (norm === cueVariants[ci].text) { land(val); return; }
			}
			var bestLen = 0, bestVariant = cueVariants[0];
			for (ci = 0; ci < cueVariants.length; ci++) {
				var m = commonPrefixLen(norm, cueVariants[ci].text);
				if (m === norm.length) {
					badAt = -1;
					input.removeClass("lrnInputErrClass");
					return;   // a live prefix under this variant — no verdict yet
				}
				if (m > bestLen) { bestLen = m; bestVariant = cueVariants[ci]; }
			}

			// A judged try is the answer commit — the first one anchors latency (0042 fix 3).
			if (!word.answeredAt) { word.answeredAt = new Date().getTime(); }
			word.lastAnswer = val;   // the typed letters, right or wrong, for the history row
			slips++;
			word.attempts = slips;   // keeps the effort count honest on the fail path too
			// The wrong character sits at norm position bestLen (== a `val` position), red, in the spot
			// the user typed it. Restarting the shake needs class off → reflow → class on: two misses in
			// a row land on a class that is already there and would otherwise animate only the once.
			badAt = bestLen;
			input.removeClass("lrnInputErrClass");
			void input[0].offsetWidth;
			input.addClass("lrnInputErrClass");

			var letterIdx = confirmedTargetChars(bestVariant.offsets, bestLen);   // the cue letter missed
			misses[letterIdx] = (misses[letterIdx] || 0) + 1;
			if (misses[letterIdx] >= R4_REVEAL_AT) {
				// The skeleton goes with the input: a disabled field under a row of ghost blanks still
				// reads as an invitation to type, and the revealed word below is the only word this
				// screen should be showing now.
				rowHost.remove();
				hint.remove();
				if (subst) { subst.remove(); }
				showRevealVerdict(body, word);
				return;
			}
			if (misses[letterIdx] === R4_AID_AT) {
				if (letterIdx === 0) {
					// An umlaut/ß first letter names its accepted substitution too ("ä — 'ae' works"),
					// so a bare-letter try ("au" for "äu") learns the digraph rule instead of a dead end.
					var firstCh = cue.core.charAt(0);
					var firstSubst = Util.umlautSubst(firstCh);
					hint.text(firstSubst !== firstCh
						? strFormat(getRes("learnQ_letterHintSubst"), firstCh, firstSubst)
						: strFormat(getRes("learnQ_letterHint"), firstCh));
				} else {
					// Naming letter 2 would hand over the whole answer, so its aid is the word itself:
					// hearing it is a real cue that still leaves the spelling to produce.
					hint.text(getRes("learnQ_hearHint"));
					playTermSound(w.termId, w.ipaVariantId);
				}
			}
		});
		input.focus();
	}

	// ── R4 on a form-set card: type the part that CHANGES ────────────────────────
	//
	// The ordinary R4 asks for the first N letters of the term. On a principal-parts card
	// ("fahren, fährt, fuhr, ist gefahren") that is "fa" — a free pass, and the reason this rung
	// exists (docs/project/verb-card-types.md, "Form-set — three changes"). Here the whole face is
	// printed with everything the forms SHARE given, and a small field standing where each form
	// changes: f[a]hren, f[ä]hrt, f[u]hr, ist gef[a]hren. That is the ablaut series — the one thing
	// the card teaches — asked directly.
	//
	// The gaps arrive as character ranges into the face (LearningService "changeGaps", computed by
	// FormSetChangingParts), so this side renders the string it already has and re-derives no
	// morphology. A verb with no changing part to ask about — "cut, cut, cut", "go, went, gone" —
	// carries no ranges at all and takes the ordinary rung above, which is why the caller checks.
	//
	// Giving the frame away is the point, not a leak: R4 has always handed over part of the answer
	// (the first letters, the pronoun), and between the choice rungs and R6's full typing this is
	// the graded step. R5's sound and R6's full type still ask for the whole thing.

	function renderChangeParts(word) {
		var w = word.w;
		markAsked(word, "MEANING_TO_TERM", cueMeaningText(w));
		var body = sessionScreen(word);
		body.addClass("lrnBodyTopClass");   // question screen — anchored like the other rungs
		body.append(promptGloss(getRes("learnQ_change"), w.gloss, w));

		// The face, literal text and fields alternating. Inline flow rather than a flex row: the
		// literal pieces carry the face's own ", " separators, so the line wraps between forms and
		// never inside one — a flex row would treat every fragment as its own wrappable item.
		var rowHost = $("<div>");
		var row = $("<div>").addClass("lrnGapRowClass");
		var gaps = [];
		var at = 0;
		for (var i = 0; i < w.changeGaps.length; i++) {
			var range = w.changeGaps[i];
			if (range[0] > at) { row.append($("<span>").text(w.term.substring(at, range[0]))); }
			gaps.push(appendGapField(row, w.term.substring(range[0], range[1]), range));
			at = range[1];
		}
		if (at < w.term.length) { row.append($("<span>").text(w.term.substring(at))); }
		rowHost.append(row);
		body.append(rowHost);

		var hint = $("<div>").addClass("lrnTypeHintClass").text(getRes("learnQ_changeHint"));
		body.append(hint);
		var subst = typeSubstLine();   // the changing part is often the umlaut itself (fahren → fährt)
		if (subst) { body.append(subst); }
		reserveReveal(body, word);

		var slips = 0;   // every wrong keystroke, across all the gaps — the 0042 effort count
		trackFirstKey(gaps[0].input, word);

		// What the learner has produced so far, written back into the face. Shaped like the answer
		// rather than as a list of fragments, so the history row can stand it beside the correct
		// face and the fragment that was wrong is legible IN PLACE — which is also why no separate
		// wrong-answer diff is needed here: a per-gap field marks its own miss where it happened.
		function typedFace() {
			var out = "", cursor = 0;
			for (var g = 0; g < gaps.length; g++) {
				out += w.term.substring(cursor, gaps[g].start) + gaps[g].input.val();
				cursor = gaps[g].end;
			}
			return out + w.term.substring(cursor);
		}

		function appendGapField(host, target, range) {
			var variants = typingVariants(target);
			var maxLen = target.length;
			for (var vi = 0; vi < variants.length; vi++) {
				maxLen = Math.max(maxLen, variants[vi].text.length);
			}
			// One character of slack over the longest accepted spelling, for the same reason R4 needs
			// it: a rejected keystroke STAYS in the field, so typing straight past it has to fit.
			var input = $("<input>")
				.attr({ type: "text", maxlength: maxLen + 1, autocomplete: "off", spellcheck: "false" })
				.addClass("lrnGapInputClass")
				.css("width", (maxLen + 1.4) + "ch");
			host.append(input);
			var gap = {
				input: input, variants: variants, start: range[0], end: range[1],
				misses: 0, badAt: -1, done: false
			};
			input.on("input", function () { judge(gap); });
			return gap;
		}

		function focusNextAfter(gap) {
			var from = gaps.indexOf(gap);
			for (var step = 1; step <= gaps.length; step++) {
				var next = gaps[(from + step) % gaps.length];
				if (!next.done) { next.input.focus(); return; }
			}
		}

		function judge(gap) {
			var val = gap.input.val();
			// A wrong keystroke stays in the field until the user overrides it, in place — the same
			// rule R4 and R6 follow. Typing straight past it arrives as an append, so splice the
			// stale bad character out before judging the new one.
			if (gap.badAt !== -1 && val.length > gap.badAt + 1) {
				val = val.slice(0, gap.badAt) + val.slice(gap.badAt + 1);
				gap.input.val(val);
			}
			var norm = typedNorm(val);   // 1:1, so every index below is valid in `val` too
			var ci;
			for (ci = 0; ci < gap.variants.length; ci++) {
				if (norm === gap.variants[ci].text) { settle(gap); return; }
			}
			// Not (yet) an answer: while the typing is still a live prefix of SOME accepted spelling
			// the user may be mid-digraph ("a" on its way to "ae" for "ä"), so stay quiet.
			gap.done = false;
			gap.input.removeClass("lrnGapDoneClass");
			var bestLen = 0;
			for (ci = 0; ci < gap.variants.length; ci++) {
				var m = commonPrefixLen(norm, gap.variants[ci].text);
				if (m === norm.length) {
					gap.badAt = -1;
					gap.input.removeClass("lrnInputErrClass");
					return;
				}
				if (m > bestLen) { bestLen = m; }
			}
			miss(gap, bestLen);
		}

		function settle(gap) {
			gap.done = true;
			gap.badAt = -1;
			gap.input.removeClass("lrnInputErrClass").addClass("lrnGapDoneClass");
			if (!word.answeredAt) { word.answeredAt = new Date().getTime(); }   // 0042 latency anchor
			for (var g = 0; g < gaps.length; g++) {
				if (!gaps[g].done) { focusNextAfter(gap); return; }
			}
			land();
		}

		function miss(gap, bestLen) {
			if (!word.answeredAt) { word.answeredAt = new Date().getTime(); }
			word.lastAnswer = typedFace();
			slips++;
			word.attempts = slips;   // keeps the effort count honest on the fail path too
			// Restarting the shake needs class off → reflow → class on, or two misses in a row land
			// on a class that is already there and animate only once.
			gap.badAt = bestLen;
			gap.input.removeClass("lrnInputErrClass");
			void gap.input[0].offsetWidth;
			gap.input.addClass("lrnInputErrClass");

			gap.misses++;
			if (gap.misses >= R4_REVEAL_AT) {
				// The skeleton goes with the fields: a row of dead inputs still reads as an
				// invitation to type, and the revealed face below is the only thing to show now.
				rowHost.remove();
				hint.remove();
				if (subst) { subst.remove(); }
				showRevealVerdict(body, word);
				return;
			}
			// Every gap's aid is the sound, and only the sound. R4 can name its first letter because
			// a second one still has to be produced; a fragment here is one to three characters, so
			// naming it IS the answer. The clip recites the whole paradigm, which is a real cue and
			// still leaves the spelling to produce.
			if (gap.misses === R4_AID_AT) {
				hint.text(getRes("learnQ_hearHint"));
				playTermSound(w.termId, w.ipaVariantId);
			}
		}

		function land() {
			word.lastAnswer = typedFace();
			word.attempts = slips + 1;   // wrong tries + the landing one
			var bloom = $("<div>").addClass("lrnBloomClass lrnBloomWideClass");
			bloom.append($("<span>").text(w.term));
			bloom.append(soundButton(w.termId, true, w.ipaVariantId));
			rowHost.empty().append(bloom);
			hint.text("");
			if (subst) { subst.remove(); }   // every gap is filled — nothing left to spell
			playTermSound(w.termId, w.ipaVariantId);
			offerAdvance(word);   // Continue / Enter moves on — no auto-advance, as on R4
		}

		gaps[0].input.focus();
	}

	// ── R6: full-word typing — build the motor pattern before the drill ──────────
	//
	// The whole word sits above the field as blanks; the user types it from memory. Each slip
	// reveals one more letter (left to right) in the word above, as a scaffold — so they always
	// get there. Deliberately un-failable ("easy" muscle-memory warm-up): typing the word out
	// passes the rung, which is the last one, so the word graduates to the drill. A leading article
	// or reflexive "sich" is shown as GIVEN text (not typed), like the R4 type-start cue.

	// The word to type = the term minus a leading reflexive "sich" (shown as a given lead). The
	// article rides along ONLY when the Practice "Nouns with article" setting is on — same switch
	// the drill uses (training_session.js isDrillArticle), so the form typed here is the form the
	// drill will ask for. With the setting off it stays a given lead and is never typed.
	function fullTypeTarget(w) {
		// A phrase types its FOCUS WORD only (§4), with the frame standing gapped above the field.
		// None of the negotiations below apply to it: a phrase carries no article, its "sich" is part
		// of the frame rather than a lead, and it is not a conjugated form.
		var typed = window.PhraseLearn ? PhraseLearn.typingPlan(w) : null;
		if (typed) {
			return { lead: "", target: typed.full, typedArticle: "", frame: typed.plan };
		}
		var typedArticle = w.article && $("#drillArticleId").is(":checked") ? w.article : "";
		var parts = w.term.split(" ");
		var lead = "", target = w.term;
		if (parts.length > 1 && fold(parts[0]) === "sich") {
			lead = "sich";
			target = parts.slice(1).join(" ");
		}
		// A conjugated form's subject pronoun follows the SAME switch Practice uses for it
		// (verbPronounPrefill, training_session.js applyAnswerPrefix): prefill on = shown as a given
		// lead, off = typed with the rest. This is the rung that mirrors the drill, so the form typed
		// here is the form the drill will ask for -- exactly the reasoning the article above follows.
		var pronoun = conjPronoun(w);
		if (pronoun && window.isVerbPronounPrefill && isVerbPronounPrefill()) {
			lead = pronoun;
			target = w.term.substring(pronoun.length + 1);
		}
		if (typedArticle) { target = typedArticle + " " + target; }
		return { lead: lead, target: target, typedArticle: typedArticle };
	}

	// 1:1 case/diacritic fold: lowercases and strips accents (ä→a, é→e). Only reachable now as the
	// LENIENT typing option (foldAnswers), where a base letter is an accepted spelling of its
	// umlaut; the strict default never offers it.
	function foldChar(s) {
		var t = s.toLowerCase();
		if (t.normalize) { t = t.normalize("NFD").replace(/[̀-ͯ]/g, ""); }
		return t;
	}

	// Case-only normalisation of typed text. Unlike foldChar this KEEPS the accent, because the
	// accent is exactly what the strict rule is about: "kümmern" and "kummern" have to stay
	// distinguishable. 1:1 on character count, so text normalised with it stays lined up
	// position-for-position with the raw text it came from (the lockstep foldChar used to provide).
	function typedNorm(s) {
		return s.toLowerCase();
	}

	// Every accepted typed spelling of `target`, built by choosing — independently at each ä/ö/ü/ß —
	// the character AS WRITTEN (ü), or the digraph substitution Practice accepts (Util.umlautSubst:
	// ä→ae, ö→oe, ü→ue, ß→ss), or — ONLY where the language rule allows it (foldAnswers) — the
	// accent-stripped base letter (ä→a). With foldAnswers off, which is the app-wide default, the
	// accepted set is exactly Practice's: the umlaut or its digraph, never the bare letter. Ordinary
	// characters get exactly one option, so a plain word yields exactly one variant. Each variant
	// carries offsets[k] = the variant's length after including target char k — the digraph
	// options are longer than 1 char, so mapping a typed length back to "how many display letters of
	// `target` were confirmed" needs this, the same way termSearchFold/foldOffsets do for the
	// dictionary search box (main.js searchPrefixToTermIndex).
	// The typing mapping for a typing rung — "ä→ae  ö→oe  ü→ue  ß→ss", the same substitutions
	// typingVariants below actually accepts, from the one table the drill and the dictionary read
	// (type_subst.js). null when the language being typed needs none, which is every language but
	// German today.
	//
	// UNDER the field here, where Practice and the dictionary put it inside: these fields are one
	// or two characters wide, so a mapping inside one would leave nowhere to type. Handed back
	// rather than appended so each rung can take it away at the moment its field goes — a landed
	// answer or a revealed word leaves nothing to type, and a spelling aid over a finished screen
	// is just clutter.
	function typeSubstLine() {
		var line = $("<div>").addClass("lrnTypeSubstClass");
		return TypeSubst.fill(line, langToCode(), TypeSubst.ANSWER) ? line : null;
	}

	function typingVariants(target) {
		var variants = [{ text: "", offsets: [] }];
		for (var i = 0; i < target.length; i++) {
			var ch = target.charAt(i);
			var exact = typedNorm(ch);                        // ü stays ü
			var digraph = typedNorm(Util.umlautSubst(ch));    // ü→ue, ß→ss (identity for plain letters)
			var options = [exact];
			if (digraph !== exact) { options.push(digraph); }
			if (foldAnswers) {
				var base = foldChar(ch);                      // ü→u — lenient rule only
				if (options.indexOf(base) < 0) { options.push(base); }
			}
			var next = [];
			for (var v = 0; v < variants.length; v++) {
				for (var o = 0; o < options.length; o++) {
					var text = variants[v].text + options[o];
					next.push({ text: text, offsets: variants[v].offsets.concat([text.length]) });
				}
			}
			variants = next;
		}
		return variants;
	}

	// Number of target characters fully confirmed by `matchLen` typed (folded) characters, per a
	// variant's offsets — mirrors main.js's searchPrefixToTermIndex, adapted to return an exact
	// completed-character count rather than an approximate highlight boundary.
	function confirmedTargetChars(offsets, matchLen) {
		var c = 0;
		while (c < offsets.length && offsets[c] <= matchLen) { c++; }
		return c;
	}

	// ── loose typing: the words are graded, the punctuation between them is not ──────────────────
	//
	// What a typed answer is graded on is the LETTERS; the separators between them are the card
	// face's presentation ("er, sie, es hat angebracht" prints its commas because a paradigm table
	// does). Practice folds them out of a submitted answer in one go (training_session.js looseKey);
	// R6 has to reach the SAME verdict keystroke by keystroke, which is what looseWalk is for — and
	// the two must agree, or a word passes the rung in one place and fails it in the other.
	//
	// Apostrophes (straight / curly / modifier) are DROPPED rather than treated as boundaries, on
	// either side, exactly as looseKey drops them: "geht's" and "gehts" are one answer.
	function isApostropheChar(ch) {
		return /['‘’ʼ]/.test(ch);
	}

	// A character that separates words rather than spells one — space, comma, hyphen, slash, period.
	// An apostrophe is deliberately NOT one: it lives inside a word.
	function isSeparatorChar(ch) {
		return !isApostropheChar(ch) && !/[\p{L}\p{M}\p{N}]/u.test(ch);
	}

	function separatorRunLen(s, i) {
		var n = 0;
		while (i + n < s.length && isSeparatorChar(s.charAt(i + n))) { n++; }
		return n;
	}

	// Walk typed text against one accepted variant, treating a RUN of separators on one side as
	// equal to a run on the other: the learner may type the face's punctuation, put a space where it
	// stands, use both, or use neither of the two the face prints — but a word boundary still has to
	// be there. That last part is what keeps this in step with looseKey, which turns punctuation
	// into a space rather than into nothing: "er,sie,es" is right, "ersiees" is not.
	//
	// Returns how far each side got before the first character that genuinely disagrees —
	// `typedUsed` indexes the typed string (where the wrong keystroke sits, for badAt),
	// `variantUsed` is a length of the variant (which target character is being missed, via its
	// offsets). The two are no longer the same number, which is exactly why a plain common-prefix
	// length can't do this job any more.
	//
	// Nothing is over-consumed: the loop stops the moment the typed side runs out, so a separator
	// the learner simply hasn't reached yet is never charged against them.
	function looseWalk(typed, variant) {
		var ti = 0, vi = 0;
		for (;;) {
			while (vi < variant.length && isApostropheChar(variant.charAt(vi))) { vi++; }
			while (ti < typed.length && isApostropheChar(typed.charAt(ti))) { ti++; }
			if (ti >= typed.length) { break; }
			var vSep = vi < variant.length && isSeparatorChar(variant.charAt(vi));
			var tSep = isSeparatorChar(typed.charAt(ti));
			if (vSep || tSep) {
				// A boundary on both sides, however each side spells it — consume both runs whole.
				// (Checked BEFORE the equal-character step: ", " against "," matches on the comma and
				// would then strand the variant's space, failing the very answer this exists to accept.)
				if (!vSep || !tSep) { break; }
				vi += separatorRunLen(variant, vi);
				ti += separatorRunLen(typed, ti);
				continue;
			}
			if (vi < variant.length && typed.charAt(ti) === variant.charAt(vi)) { ti++; vi++; continue; }
			break;
		}
		return { typedUsed: ti, variantUsed: vi };
	}

	// Is a walk a finished answer — everything typed accounted for, and nothing left of the variant
	// but separators (a face ending in punctuation doesn't ask for it to be typed)?
	function looseComplete(walk, typed, variant) {
		if (walk.typedUsed !== typed.length) { return false; }
		for (var i = walk.variantUsed; i < variant.length; i++) {
			var ch = variant.charAt(i);
			if (!isSeparatorChar(ch) && !isApostropheChar(ch)) { return false; }
		}
		return true;
	}

	// How many non-letters `text` holds — the extra room a typing field needs, since a learner may
	// add separators the face doesn't print (a space before a comma, a comma between forms).
	function separatorCount(text) {
		var n = 0;
		for (var i = 0; i < text.length; i++) {
			if (!/[\p{L}\p{M}\p{N}]/u.test(text.charAt(i))) { n++; }
		}
		return n;
	}

	// Longest common prefix length between two strings, capped at both lengths.
	function commonPrefixLen(a, b) {
		var n = Math.min(a.length, b.length), m = 0;
		while (m < n && a.charAt(m) === b.charAt(m)) { m++; }
		return m;
	}

	// A masked character is "structure" (always shown) if it isn't a letter — spaces, hyphens,
	// apostrophes, periods keep the word's shape visible while the letters stay blank.
	function isTypeLetter(ch) {
		return /[A-Za-zÀ-ÖØ-öø-ÿ]/.test(ch);
	}

	// R6: the slots ARE the field. One dotted underline per character of the target, with the real
	// <input> laid over them invisibly — it still owns the keyboard, IME, paste and mobile focus, but
	// nothing is ever typed anywhere except into the word itself.
	//
	// It used to be two lines: the word as blanks above, a separate input below, and the answer had to
	// be typed in full into the input even though the blanks were the surface showing revealed
	// letters. That reads as two competing copies of the word, and learners split between them —
	// deleting what they had typed to "continue" the revealed prefix, or trying to type on the blanks
	// (owner report 2026-08-12). R4 never had the problem: its field sits inline in the word row.
	//
	// The rules the single line runs on:
	//   · the caret sits on the letter the rung is waiting for and does NOT move until it is right;
	//   · a rejected keystroke never enters the answer — it is shown in that slot, red, and reverted;
	//   · a second miss on the same letter hands it over, subdued, in the slot the learner still owes,
	//     and the rejected keystroke moves above the slot, struck through, tracking each further miss;
	//   · a correct letter turns green and the caret steps on.
	function renderFullType(word) {
		var w = word.w;
		markAsked(word, "MEANING_TO_TERM", cueMeaningText(w));
		var body = sessionScreen(word);
		body.addClass("lrnBodyTopClass");   // question screen — anchored like the choice rungs
		body.append(promptGloss(getRes("learnQ_typeFull"), w.gloss, w));

		var spec = fullTypeTarget(w);
		var target = spec.target;
		// Kept by reference: on success the frame becomes its own revealed form (the answer filled
		// in place) instead of a second, separate reveal stacked below it — see succeed().
		var frameEl = spec.frame ? frameLine(spec.frame, false) : null;
		if (frameEl) { body.append(frameEl); }
		// Every folded spelling accepted for `target` — the plain accent-stripped one plus, at each
		// ä/ö/ü/ß, the Practice-style digraph (see typingVariants). maxTypedLen is the longest of
		// them, in raw characters (fold never changes character count) — the field must fit "ss" for
		// a target that only has one "ß" in it.
		var variants = typingVariants(target);
		var maxTypedLen = target.length;
		for (var vi = 0; vi < variants.length; vi++) { maxTypedLen = Math.max(maxTypedLen, variants[vi].text.length); }

		var rowHost = $("<div>").addClass("lrnFullTypeHostClass");
		var row = $("<div>").addClass("lrnTypeRowClass");
		if (w.article && !spec.typedArticle) { row.append($("<span>").addClass("lrnTypeLeadClass").text(w.article)); }
		if (spec.lead) { row.append($("<span>").addClass("lrnTypeLeadClass").text(spec.lead)); }

		// The word as slots — one per character, each an underline the letter sits ON rather than a
		// blank the letter replaces, so the dotted line stays whole while it fills.
		var slotRow = $("<span>").addClass("lrnMaskRowClass");
		row.append(slotRow);
		rowHost.append(row);

		// One slot of headroom per separator in the face: punctuation is graded loosely, so a learner
		// may type a space AND the comma it stands for. Plus one character — a rejected keystroke is
		// reverted rather than left in the field, but maxlength silently swallows a keystroke at a full
		// field, and a swallowed keystroke never reaches the handler that would count it as a miss.
		var typeSlack = 1 + separatorCount(target);
		var input = $("<input>").attr({
			type: "text", autocomplete: "off", spellcheck: "false",
			autocapitalize: "off", autocorrect: "off", maxlength: maxTypedLen + typeSlack
		}).addClass("lrnFullInputClass");
		rowHost.append(input);   // absolutely positioned over the row — see .lrnFullTypeHostClass
		body.append(rowHost);

		var hint = $("<div>").addClass("lrnTypeHintClass")
			.text(getRes(spec.frame ? "learnQ_typeFullGapHint" : "learnQ_typeFullHint"));
		body.append(hint);
		// The one rung that asks for the WHOLE spelling from memory, so the one that needs this most.
		// It is not a hint about the word: it says nothing about whether THIS word has an umlaut,
		// only how the language's letters are typed (see type_subst.js).
		var subst = typeSubstLine();
		if (subst) { body.append(subst); }

		// NOTHING else goes on screen while the user is still typing: this rung asks for the whole word
		// from memory, so any control offered mid-word is either a hint or a distraction. The word's
		// audio in particular would dictate the letters — it only arrives once the answer is in
		// (see succeed), together with the "type it again" offer.

		var accepted = "";   // the field content the rung has taken — only ever a valid loose prefix
		var revealed = 0;    // how many leading target characters have been handed over after a 2nd miss
		var badChar = "";    // the rejected keystroke currently on show ("" = none)
		var badIdx = -1;     // the slot it is charged to
		var slips = 0;       // wrong keystrokes — the 0042 R6 effort count
		var letterMiss = []; // wrong keystrokes charged to each target letter; the 2nd reveals it
		trackFirstKey(input, word);   // 0042: first_key_ms measured in the rung's own input

		// Where the typing has got to, in TARGET characters: how many are confirmed, and what raw
		// characters are in flight on the next one (a half-typed digraph — the "u" of "ue" for "ü").
		// Read off whichever accepted variant got furthest, so the umlaut and its digraph spelling
		// land on the same slot rather than on two different ones.
		function progress() {
			var fval = typedNorm(accepted);
			var done = 0, used = 0, offs = variants[0].offsets, any = false;
			for (var i = 0; i < variants.length; i++) {
				var wk = looseWalk(fval, variants[i].text);
				if (wk.typedUsed !== fval.length) { continue; }   // not a live prefix of this variant
				var c = confirmedTargetChars(variants[i].offsets, wk.variantUsed);
				if (!any || c > done || (c === done && wk.variantUsed > used)) {
					any = true; done = c; used = wk.variantUsed; offs = variants[i].offsets;
				}
			}
			var base = done > 0 ? offs[done - 1] : 0;
			var pend = Math.max(0, used - base);   // > 0 only mid-digraph, which is always 1:1 with the typing
			return { done: done, pending: pend > 0 ? accepted.slice(accepted.length - pend) : "" };
		}

		// The caret sits on the first unconfirmed target character — EVERY character, separators
		// included. looseWalk is loose about which separator is typed and how many, but not about
		// whether one is there at all ("er,sie,es" passes, "ersiees" does not), so a space is a slot
		// the rung genuinely waits on. Stepping the caret past it to the next letter would point at a
		// character that cannot be accepted yet, and the learner would miss on it forever.
		function paint() {
			var st = progress();
			var cur = st.done;
			slotRow.empty();
			for (var i = 0; i < target.length; i++) {
				var ch = target.charAt(i);
				var gap = (ch === " ");
				var stuck = (badChar && badIdx === i);
				var slot = $("<span>").addClass("lrnMaskSlotClass");
				var text = "";
				if (gap) { slot.addClass("lrnMaskGapClass"); }   // word gap — keeps its column, prints nothing
				if (i < st.done) {
					slot.addClass("lrnMaskDoneClass");                        // typed and right — green
					text = gap ? "" : ch;
				} else if (i === cur && stuck && letterMiss[i] === 1) {
					// First slip: the letter the learner actually typed, red, where they typed it.
					slot.addClass("lrnMaskBadClass");
					text = badChar;
				} else if (i === cur && st.pending) {
					slot.addClass("lrnMaskPendingClass");
					text = st.pending;
				} else if (i < revealed || !isTypeLetter(ch)) {
					// Handed over after a second miss — or a hyphen/space, which the word's shape shows
					// from the start and which no reveal has to give away.
					slot.addClass("lrnMaskGivenClass");
					text = gap ? "" : ch;
				}
				// Never truly empty: a slot with no character still has to hold its underline open.
				slot.text(text || " ");
				if (i === cur) { slot.addClass("lrnMaskCursorClass"); }
				if (i === cur && stuck && letterMiss[i] >= 2) {
					// From the second slip on, the rejected keystroke moves above the slot so the given
					// letter can occupy it; it is struck through, and tracks every further wrong key.
					slot.append($("<span>").addClass("lrnMaskStruckClass").text(badChar));
				}
				slotRow.append(slot);
			}
		}
		paint();

		function shake() {
			slotRow.removeClass("lrnShakeClass");
			void slotRow[0].offsetWidth;   // restart the animation when the same slot is missed again
			slotRow.addClass("lrnShakeClass");
		}

		function succeed() {
			word.answeredAt = new Date().getTime();   // the commit — the 0042 latency anchor
			word.attempts = slips + 1;                // R6 effort counter: wrong keystrokes + the landing one
			// A phrase's answer is the focus word it actually typed, not the whole phrase.
			word.lastAnswer = spec.frame ? target
				: (spec.typedArticle ? spec.typedArticle + " " + w.term : w.term);
			input.remove();
			hint.text("");
			if (subst) { subst.remove(); }   // the word is typed — nothing left to spell
			if (frameEl) {
				// The frame IS the reveal — filled in place, same styling the meaning rungs use for
				// a revealed gap — instead of a whole second copy of the sentence stacked below it
				// (the masked-word row it replaces sat right where that duplicate used to land;
				// owner report 2026-07-31: "why show the gapped sentence AND the filled one?").
				var revealedFrame = frameLine(spec.frame, true);
				revealedFrame.find("._gapFrameClass").append(soundButton(w.termId, true, w.ipaVariantId));
				frameEl.replaceWith(revealedFrame);
				rowHost.remove();
			} else {
				var bloom = $("<div>").addClass("lrnBloomClass");
				if (w.article) { bloom.append($("<span>").addClass("lrnTypeLeadClass").text(w.article)); }
				bloom.append($("<span>").text(w.term));
				bloom.append(soundButton(w.termId, true, w.ipaVariantId));   // app-wide big volume icon, not a 🔊 emoji
				rowHost.empty().append(bloom);
			}
			playTermSound(w.termId, w.ipaVariantId);
			// Only NOW is "repeat the word" offered, and it repeats the RUNG, not the audio (the bloom's
			// own sound icon plays it): typing the whole word once is rarely enough to own the spelling,
			// so the user who wants another go gets it on the spot — same word, same rung, fresh field,
			// no ladder move and no event. Nothing advances by itself; Continue (top right, or Enter)
			// stays the only way forward.
			var repeatRow = $("<div>").addClass("lrnRepeatRowClass");
			repeatRow.append($("<button>").attr("type", "button").addClass("pl-btn-quiet lrnRepeatButtonClass")
				.text(getRes("learnQ_repeatWord"))
				.click(function () { renderFullType(word); }));
			body.append(repeatRow);
			offerAdvance(word);   // Continue / Enter → passWord graduates it (this is the last rung)
		}

		// The slots are a display, not a text cursor: the answer is only ever built at its end, so a
		// click on the word — or an arrow key — puts the caret back where the typing is. Without this,
		// a caret parked mid-value would insert there and the whole walk below would read the wrong
		// character as the rejected one.
		function caretToEnd() {
			var el = input[0], n = el.value.length;
			try { el.setSelectionRange(n, n); } catch (e) { /* not every field type allows it */ }
		}
		input.on("focus mouseup keyup", caretToEnd);

		input.on("keydown", function (e) {
			// Backspace retires the rejected keystroke on show before it touches the answer: the learner
			// sees a red letter sitting in the slot, and deleting it has to delete THAT — not the last
			// letter they got right, which is all the field actually holds.
			if (e.which === 8 && badChar) {
				badChar = ""; badIdx = -1;
				paint();
				e.preventDefault();
			}
		});

		input.on("input", function () {
			var raw = input.val();
			var fval = typedNorm(raw);   // typedNorm is 1:1, so every index below is valid in `raw` too.

			// Check fval against every accepted variant, under looseWalk — so the face's punctuation
			// may be typed, replaced by a space or left out, and only the letters are graded. A
			// FINISHED walk on any variant passes the rung outright, whatever the other variants say.
			// Short of that, if fval is still a valid (loose) prefix of at least one variant there is
			// no error yet — stay quiet, since it could still resolve either way (typing "a" for "ä":
			// under the lenient rule that is valid so far under both the bare "a" variant and the "ae"
			// digraph variant; under the strict rule only the "ae" variant keeps it alive).
			var i, walk, alive = false;
			for (i = 0; i < variants.length; i++) {
				walk = looseWalk(fval, variants[i].text);
				if (looseComplete(walk, fval, variants[i].text)) {
					accepted = raw;
					succeed();
					return;
				}
				if (walk.typedUsed === fval.length) { alive = true; }
			}
			if (alive) {
				accepted = raw;
				badChar = ""; badIdx = -1;   // a correct keystroke retires the slip on show
				paint();
				return;
			}

			// No variant accepts it: a genuine wrong keystroke. It never enters the answer — the field
			// goes back to what it held — so the caret cannot drift off the letter still owed, which is
			// the whole point of the single line. Nothing but an append can be judged this way, so an
			// edit that shortens or rewrites the value (select-all, paste over) is simply reverted.
			var bad = raw.length > accepted.length ? raw.charAt(accepted.length) : "";
			input.val(accepted);
			if (!bad) { paint(); return; }
			slips++;   // every wrong keystroke — reaches the server as `attempt` (0042 fix 4)
			var st = progress();
			var idx = st.done;   // the slot the caret is on — the character the rung is waiting for
			// The letter is handed over on the SECOND miss of it, not the first: one wrong keystroke is
			// a slip of the fingers and deserves another go at remembering, two is genuinely stuck, and
			// that is when the scaffold earns its keep. The rung still cannot be failed — every letter
			// arrives eventually — it just costs two tries instead of one.
			letterMiss[idx] = (letterMiss[idx] || 0) + 1;
			if (letterMiss[idx] >= 2) {
				revealed = Math.max(revealed, Math.min(idx + 1, target.length));
				// A half-typed digraph is rolled back with the hand-over: the slot is about to show the
				// whole letter, so the learner must be free to spell it either way ("ü" or "ue") from a
				// clean start rather than having to guess which half the field is still holding.
				if (st.pending) {
					accepted = accepted.slice(0, accepted.length - st.pending.length);
					input.val(accepted);
				}
			}
			badChar = bad; badIdx = idx;
			paint();
			shake();
		});
		input.focus();
	}

	// ── recap + handoff ──────────────────────────────────────────────────────────

	function finishSession() {
		var words = session.words;
		var elapsed = Math.round((new Date().getTime() - session.t0) / 1000);
		var sid = session.sid;
		session = null;
		if (window.VISIT_LOG) { VISIT_LOG.mark("recap", { words: words.length, secs: elapsed }); }

		// The session's LEARN_DONE row — the server's only record that a Learn session finished.
		// Statistics, never load-bearing, and never shown as an error. Through PlConn as a barrier:
		// it goes after every step of the session, and again if it could not be sent.
		PlConn.post("api/learning/session-done", { clientSessionId: sid }, { barrier: true });

		// A Learn session just completed: bump the onboarding attr's counter. The guided
		// walk's 3-word first scoop (OB_GUIDE.firstRun) retires on exactly this signal —
		// "3 words" is for the FIRST learn session only, later scoops offer the normal 5.
		// The count is the words actually learned: one banned mid-session was not.
		if (window.ONBOARDING) {
			ONBOARDING.tips.learnFinished(words.filter(function (x) { return !x.known; }).length);
		}

		var readyCards = [];
		var readyCount = 0;
		var pills = [];
		for (var i = 0; i < words.length; i++) {
			var w = words[i].w;
			if (words[i].known) { continue; }   // banned mid-session — not learned here, not ready
			if (words[i].done) {
				readyCount++;   // counts WORDS graduated (one ladder word = one word in both modes)
				// Hand the graduated senses to Practice: in term mode that is every sense of the word,
				// so the whole term arrives as one drill unit; in sense mode it is the single sense.
				var sids = cardSenseIds(w);
				for (var s = 0; s < sids.length; s++) {
					readyCards.push({ senseId: sids[s], termId: w.termId });
				}
			}
			pills.push(w);
		}

		// The just-learned words STAY in the learning tray so "back to the words" shows exactly what
		// was in the session (graduated rows carry the "ready" badge). They only leave the tray
		// when the user takes them to Practice (takePractice), where they move as one batch — so the
		// hand-off carries "exactly these words" and the learn tray is left clean afterwards.
		// Remember what just graduated so the tray offers "Go to the practice now!" (same handoff as
		// the recap's Drill button) and so takePractice knows which rows to move out.
		pendingPractice = readyCards.length ? readyCards.slice() : null;

		// Graduating words gives them their first "Ready" shoot, so every other surface showing
		// them (open word lists, the trays) is now stale — repaint them all in one batched call
		// rather than making the user close and reopen a list. Same hook as the drill's.
		GARDEN.refreshAll();
		// A graduated word also MOVES between My Garden's sections ("In progress" → "Ready"),
		// which an icon repaint cannot express — reload the list itself if it is open.
		if (window.refreshGardenList) { window.refreshGardenList(); }

		hideCta();   // the recap owns its own buttons — no tray control band
		var area = root().empty();
		// The recap gets a body of its own so a word pill can open a rich card exactly the way every
		// rung does — bindTermStudy hides the body's children and renders the excursion in their
		// place, which needs a container that is not the root. Deliberately NOT .lrnBodyClass: that
		// caps the width at 900px and the recap is wider than that by design.
		var body = $("<div>").addClass("lrnRecapBodyClass");
		area.append(body);
		var card = $("<div>").addClass("lrnRecapClass");
		// Heading mark = the ladder's sprout (GARDEN.markIcon), the same image the Learn tab and
		// the "Add to Learning" pill use — never an emoji, which renders as whatever glyph the OS
		// ships and looks nothing like the plant art the rest of the screen is made of.
		card.append($("<div>").addClass("lrnRecapHeadClass")
			.append($("<span>").text(strFormat(getRes("learnRecap_head"), pills.length)))
			.append($(GARDEN.markIcon("sprout"))));

		var stats = $("<div>").addClass("lrnRecapStatsClass");
		stats.append(statCell(pills.length, getRes("learnRecap_met")));
		stats.append(statCell(readyCount, getRes("learnRecap_ready")));
		stats.append(statCell(fmtTime(elapsed), getRes("learnRecap_time")));
		card.append(stats);

		// Each pill's TERM is a study link into its rich entry — the recap lists the words you just
		// met, and "what does that one actually mean" is the question it invites. Same underline,
		// tooltip and overlay as a rung's prompt term, so a term reads and behaves the same wherever
		// Learn shows one. The gloss half stays inert: it is the answer, not a word to look up.
		//
		// A PHRASE pill links word by word instead (phraseStudySpan): the phrase's own entry is the
		// translation already printed beside it on this very row, and the words inside it are what the
		// learner is actually asking about. Verb cards are excluded — "ich brenne ab" is stored as a
		// phrase Term but is studied through its head verb, which bindTermStudy already opens.
		var pillRow = $("<div>").addClass("lrnRecapWordsClass");
		for (var p = 0; p < pills.length; p++) {
			var pill = $("<span>").addClass("lrnRecapWordClass");
			var link;
			if (pills[p].phrase && !studiesHeadVerb(pills[p])) {
				link = phraseStudySpan(displayTerm(pills[p]), body);
			} else {
				link = $("<span>").addClass("lrnHwDrillClass").text(displayTerm(pills[p]));
				link.assignTooltip("learnStudyTerm_tooltip");
				bindTermStudy(link, pills[p], body);   // per-iteration `w` — closes over the parameter
			}
			// cueMeaningText, not the raw gloss: a conjugated card's own translation is the invented
			// one, so the pill names the head verb's first sense like every other surface does.
			pill.append(link).append($("<span>").text(" · " + firstWordOf(cueMeaningText(pills[p]))));
			pillRow.append(pill);
		}
		card.append(pillRow);

		card.append($("<div>").addClass("lrnRecapBridgeClass").text(getRes("learnRecap_bridge")));

		var buttons = $("<div>").addClass("lrnRecapButtonsClass");
		var back = $("<button>").attr("type", "button").addClass("pl-btn-pill")
			.text(getRes("learnRecap_back"))
			.click(function () {
				sessionChrome(false);
				showTray();
			});
		var drill = $("<button>").attr("type", "button").addClass("lrnCtaButtonClass")
			.text(getRes("learnRecap_drillNow"))
			.prop("disabled", !readyCards.length)
			.click(function () {
				sessionChrome(false);
				takePractice(readyCards);
			});
		buttons.append(back).append(drill);
		card.append(buttons);

		body.append(card);

		// The recap is ON STAGE now — re-run the cue engines. The hideCta() call above fired
		// its evaluate too early on purpose-built grounds (clearing session-screen beacons)
		// while the ladder's .lrnBodyClass was still in the DOM, so the stage rule wiped
		// everything and nothing re-armed: the guided walk's recap step ("Go to the practice
		// now!" glows) never lit without this hook.
		if (window.OB_TIPS) { OB_TIPS.evaluate(); }
	}

	function statCell(value, label) {
		return $("<div>").addClass("stats-cell")
			.append($("<span>").addClass("stats-num").text(value))
			.append($("<span>").addClass("stats-lbl").text(label));
	}

	function fmtTime(sec) {
		var m = Math.floor(sec / 60);
		var s = sec % 60;
		return m + ":" + (s < 10 ? "0" : "") + s;
	}

	function firstWordOf(gloss) {
		return (gloss || "").split(",")[0];
	}

	// ── the soft-gate nudge on the Practice tray ─────────────────────────────────

	// Called by main.js after the Practice tray renders. Counts the tray's brand-new words —
	// none of their staged sense cards learned, the word never drilled — and offers a one-click
	// detour into the Learn tab (the fresh cards move over).
	function maybeNudge(defArea, defList) {
		var ids = [];
		for (var i = 0; i < defList.length; i++) {
			var c = defList[i];
			if (c && c.senseId != null && c.term && c.term.id != null) {
				ids.push(c.senseId);
			}
		}
		if (!ids.length) { return; }
		// The status round-trip is async and #defId is shared across modes: if the list was
		// re-rendered meanwhile (tab switch, new set), this response is stale — don't prepend.
		var anchor = defArea.children().first()[0];
		$.ajax("api/learning/sense-status", {
			type: "POST",
			data: { senseIds: ids },
			dataType: "json",
			traditional: true,
			success: function (data) {
				if (!data || !data.senses) { return; }
				var statusBySense = {};
				for (var i = 0; i < data.senses.length; i++) {
					statusBySense[data.senses[i].senseId] = data.senses[i];
				}
				// A word only counts as "brand new" if NONE of its staged senses have been
				// touched. A term with one graduated (thirsty) sense and another never-drilled
				// sense is a familiar word with an unmet sense, not a new word — grouping by
				// term first (rather than flagging on any one fresh sense) keeps that distinction.
				var byTerm = {};
				for (var j = 0; j < defList.length; j++) {
					var c = defList[j];
					if (c && c.senseId != null && c.term && c.term.id != null) {
						(byTerm[c.term.id] = byTerm[c.term.id] || []).push(c);
					}
				}
				var fresh = [];
				var freshTermCount = 0;
				for (var termId in byTerm) {
					var cards = byTerm[termId];
					var allFresh = cards.every(function (card) {
						var s = statusBySense[card.senseId];
						return s && !s.started && !s.seenInDrill && !s.graduated && !s.known && !s.termPlanted;
					});
					if (allFresh) {
						freshTermCount++;
						fresh = fresh.concat(cards);
					}
				}
				if (!fresh.length) { return; }
				if (!anchor || !document.contains(anchor) || defArea.children().first()[0] !== anchor) {
					return;   // the list changed under us — stale response
				}
				var chip = $("<div>").addClass("lrnNudgeClass");
				chip.append($(GARDEN.markIcon("sprout")));
				chip.append($("<span>").text(strFormat(getRes("learnNudge_message"), freshTermCount)));
				chip.append($("<button>").attr("type", "button").addClass("pl-btn-pill lrnNudgeActionClass")
					.text(getRes("learnNudge_action"))
					.click(function () {
						addWords(fresh);
						if (window.openLearnTab) { window.openLearnTab(); }
					}));
				// One chip at a time. A tray APPEND re-asks with the grown list rather than
				// re-rendering the box (main.js appendPracticeRows), so a chip from the previous add
				// may still be standing with the old count — replace it in the same frame it is
				// removed, so the count updates without the chip blinking out and back.
				defArea.children(".lrnNudgeClass").remove();
				defArea.prepend(chip);
			},
			error: function () { /* the nudge is optional — stay quiet */ }
		});
	}
})();
